import React, { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";
import "@/App.css";
import CareConnections from "@/care/CareConnections";
import Landing from "@/Landing";
import DaycareOps from "@/modules/DaycareOps";
import ParentConnect from "@/modules/ParentConnect";
import About from "@/About";
import ContactPage from "@/Contact";
import Storefront from "@/store/Storefront";
import PaymentResult from "@/store/PaymentResult";
import SignInModal, { ClaimModal, ResetModal } from "@/SignInModal";
import BroadcastPopup from "@/BroadcastPopup";
import UpcomingLiveBanner from "@/UpcomingLiveBanner";
import AdSlot from "@/AdSlot";
import MasterDash from "@/MasterDash";
import AdminInbox from "@/AdminInbox";
import FacilityDash from "@/FacilityDash";
import ProviderDirectory from "@/ProviderDirectory";
import LegalPage from "@/LegalPage";
import { applyPageMeta } from "@/lib/meta";
import NativeMeeting, { openNativeMeeting } from "@/modules/NativeMeeting";
import { LiveViewer } from "@/modules/LiveEvents";
import LivePiP from "@/modules/LivePiP";
import { SiteSearch } from "@/SiteSearch";
import { goLiveNav, liveLocAllowed } from "@/modules/LiveEvents";
import { Toaster, toast } from "sonner";
import ContactButtons from "@/lib/ContactButtons";
import ErrorBoundary from "@/components/ErrorBoundary";
import { enablePush, fetchUnreadCount, syncAppBadge } from "@/lib/push";
import SocialLinks, { SOCIAL_PLATFORMS } from "@/lib/SocialLinks";
import {
  Users,
  ShieldCheck,
  Building2,
  CreditCard,
  Bell,
  Sun,
  Moon,
  Trash2,
  Upload,
  CheckCircle2,
  AlertTriangle,
  Plus,
  X,
  Activity,
  Camera,
  UserPlus,
  Baby,
  HeartHandshake,
  ShoppingBag,
  Rocket,
  LogOut,
  Pencil,
  MessageCircle,
  Lock,
  Video,
  Download,
  Menu,
} from "lucide-react";

// ==========================================
// MOCK SEED DATA (FOR TESTING PRIOR TO WIPEOUT)
// ==========================================

const MOCK_OWNER_DATA = {
  facilityName: "Sunshine Academy - Main Campus",
  licenseNumber: "DAY-CA-883019",
  jurisdiction: "California Dept. of Social Services",
  taxId: "94-3829102",
  maxCapacity: 120,
  licenseDocumentBase64: null,
  isVerified: true,
};

const MOCK_PROFILES = [
  {
    id: "usr_1",
    organizationId: "org_1",
    fullName: "Elena Rostova",
    role: "admin",
    avatarBase64:
      "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB3b21hbiUyMHBvcnRyYWl0JTIwc21pbGluZ3xlbnwwfHx8fDE3ODgwMzY3NjV8MA&ixlib=rb-4.1.0&q=85",
    email: "elena@sunshineacademy.com",
    phone: "+1 (555) 019-2834",
    isActive: true,
  },
  {
    id: "usr_2",
    organizationId: "org_1",
    fullName: "Marcus Vance",
    role: "teacher",
    avatarBase64:
      "https://images.unsplash.com/photo-1614023342667-6f060e9d1e04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBtYW4lMjBwb3J0cmFpdCUyMHNtaWxpbmd8ZW58MHx8fDE3ODgwMzY3NjV8MA&ixlib=rb-4.1.0&q=85",
    email: "marcus@sunshineacademy.com",
    phone: "+1 (555) 018-9921",
    isActive: true,
  },
  {
    id: "usr_3",
    organizationId: "org_1",
    fullName: "Sarah Jenkins",
    role: "parent",
    avatarBase64:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjB3b21hbiUyMHBvcnRyYWl0JTIwc21pbGluZ3xlbnwwfHx8fDE3ODgwMzY3NjV8MA&ixlib=rb-4.1.0&q=85",
    email: "sarah.j@example.com",
    phone: "+1 (555) 014-4432",
    isActive: true,
  },
];

const MOCK_CHILDREN = [
  {
    id: "ch_1",
    organizationId: "org_1",
    classroomId: "cls_toddlers",
    firstName: "Leo",
    lastName: "Jenkins",
    dob: "2023-04-12",
    photoBase64:
      "https://images.unsplash.com/photo-1566513857792-9a52cd97e06d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxjdXRlJTIwdG9kZGxlciUyMHBvcnRyYWl0fGVufDB8fHx8MTc4ODAzNjc2NXww&ixlib=rb-4.1.0&q=85",
    allergies: ["Peanuts", "Dairy"],
    medicalNotes: "Requires Epipen in main classroom storage.",
    emergencyContacts: [{ name: "Sarah Jenkins", phone: "+1 (555) 014-4432", relation: "Mother" }],
  },
  {
    id: "ch_2",
    organizationId: "org_1",
    classroomId: "cls_preschool",
    firstName: "Mia",
    lastName: "Torres",
    dob: "2022-08-03",
    photoBase64:
      "https://images.unsplash.com/photo-1599191230779-c1ffc792bc83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxjdXRlJTIwdG9kZGxlciUyMHBvcnRyYWl0fGVufDB8fHx8MTc4ODAzNjc2NXww&ixlib=rb-4.1.0&q=85",
    allergies: [],
    medicalNotes: "",
    emergencyContacts: [],
  },
];

const MOCK_INVOICES = [
  {
    id: "inv_101",
    parentId: "usr_3",
    parentName: "Sarah Jenkins",
    amount: 1450.0,
    status: "paid",
    dueDate: "2026-09-01",
    description: "Full-time Toddler Tuition - Sept 2026",
  },
  {
    id: "inv_102",
    parentId: "usr_3",
    parentName: "Sarah Jenkins",
    amount: 150.0,
    status: "pending",
    dueDate: "2026-09-15",
    description: "After-school Activity & Material Fee",
  },
];

const MOCK_ALERTS = [
  { id: "alt_1", type: "warning", message: "License re-certification due in 45 days.", timestamp: "10m ago" },
  { id: "alt_2", type: "danger", message: "Immunization record missing for 2 new enrollments.", timestamp: "1h ago" },
];

// ==========================================
// STATE MANAGEMENT CONTEXT
// ==========================================

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const isLive = () => true; // site is live for real users — mock seed data is permanently disabled

  const readStored = (key) => {
    try {
      return JSON.parse(localStorage.getItem(key) || "null");
    } catch {
      return null;
    }
  };  const [isProductionMode, setIsProductionMode] = useState(isLive);
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem("nt_theme") || "light";
    } catch {
      return "light";
    }
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [accountsDeleted, setAccountsDeleted] = useState(isLive);
  const [ownerData, setOwnerData] = useState(() => readStored("nt_owner"));
  const [profiles, setProfiles] = useState(() => readStored("nt_profiles") || []);
  const [childrenList, setChildrenList] = useState(() => readStored("nt_children") || []);
  const [invoices, setInvoices] = useState(() => readStored("nt_invoices") || []);
  const [alerts, setAlerts] = useState(() => readStored("nt_alerts") || []);
  const [rooms, setRooms] = useState(() => readStored("nt_rooms") || []);
  const [checkins, setCheckins] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [messages, setMessages] = useState([]);

  // Go Live is permanent: mock data and demo accounts are disabled at the source.
  const toggleProductionMode = () => {};

  const toggleTheme = () => {
    setTheme((prev) => {
      const next = prev === "light" ? "dark" : "light";
      try {
        localStorage.setItem("nt_theme", next);
      } catch (err) {
        console.warn("Theme preference could not be persisted locally", err);
      }
      if (currentUser?.real) {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me/theme`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ theme: next }),
        }).catch(() => {});
      }
      return next;
    });
  };

  const applyTheme = (t) => {
    if (t !== "light" && t !== "dark") return;
    setTheme(t);
    try {
      localStorage.setItem("nt_theme", t);
    } catch (err) {
      console.warn("Theme preference could not be persisted locally", err);
    }
  };

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  // Persist daycare records locally so they survive refresh/sign-out
  useEffect(() => {
    localStorage.setItem("nt_profiles", JSON.stringify(profiles));
  }, [profiles]);
  useEffect(() => {
    localStorage.setItem("nt_children", JSON.stringify(childrenList));
  }, [childrenList]);
  useEffect(() => {
    localStorage.setItem("nt_invoices", JSON.stringify(invoices));
  }, [invoices]);
  useEffect(() => {
    localStorage.setItem("nt_owner", JSON.stringify(ownerData));
  }, [ownerData]);
  useEffect(() => {
    localStorage.setItem("nt_alerts", JSON.stringify(alerts));
  }, [alerts]);
  useEffect(() => {
    localStorage.setItem("nt_rooms", JSON.stringify(rooms));
  }, [rooms]);

  // ---------- Workspace persistence (MongoDB) for signed-in facility users ----------
  const wsApi = async (path, method = "POST", body) => {
    const facilityOverride = sessionStorage.getItem("nt_facility_override");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workspace/${path}`, {
      method,
      headers: {
        ...(body ? { "Content-Type": "application/json" } : {}),
        ...(facilityOverride ? { "X-Facility-Id": facilityOverride } : {}),
      },
      credentials: "include",
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      const b = await res.json().catch(() => ({}));
      throw new Error(typeof b.detail === "string" ? b.detail : res.status >= 500 ? "Server busy — please try again in a moment." : "Request failed");
    }
    return res.json();
  };

  const syncWorkspace = useCallback(async () => {
    if (!currentUser?.real) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workspace`, {
        credentials: "include",
        headers: sessionStorage.getItem("nt_facility_override") ? { "X-Facility-Id": sessionStorage.getItem("nt_facility_override") } : {},
      });
      if (!res.ok) return;
      const ws = await res.json();
      setProfiles(ws.profiles || []);
      setChildrenList(ws.children || []);
      setInvoices(ws.invoices || []);
      setOwnerData(ws.owner || null);
      setAlerts(ws.alerts || []);
      setRooms(ws.rooms || []);
      setCheckins(ws.checkins || []);
      setMeetings(ws.meetings || []);
      setMessages(ws.messages || []);
    } catch (err) {
      console.error("Workspace sync failed", err);
    }
  }, [currentUser]);

  useEffect(() => {
    syncWorkspace();
  }, [syncWorkspace]);

  const wsActions = {
    addRoom: async (data) => {
      const doc = await wsApi("room", "POST", data);
      setRooms((prev) => [...prev, doc]);
    },
    updateRoom: async (id, data) => {
      await wsApi(`room/${id}`, "PATCH", data);
      setRooms((prev) => prev.map((r) => (r.id === id ? { ...r, ...data } : r)));
    },
    deleteRoom: async (id) => {
      await wsApi(`room/${id}`, "DELETE");
      setRooms((prev) => prev.filter((r) => r.id !== id));
      setChildrenList((prev) => prev.map((c) => (c.roomId === id ? { ...c, roomId: undefined } : c)));
    },
    assignChildToRoom: async (roomId, childId) => {
      await wsApi(`room/${roomId}/assign`, "POST", { childId });
      setChildrenList((prev) => prev.map((c) => (c.id === childId ? { ...c, roomId } : c)));
    },
    unassignChild: async (childId) => {
      await wsApi("room/unassign", "POST", { childId });
      setChildrenList((prev) => prev.map((c) => (c.id === childId ? { ...c, roomId: undefined } : c)));
    },
    addInvoice: async (data) => {
      const doc = await wsApi("invoice", "POST", data);
      setInvoices((prev) => [doc, ...prev]);
    },
    updateInvoice: async (id, data) => {
      await wsApi(`invoice/${id}`, "PATCH", data);
      setInvoices((prev) => prev.map((i) => (i.id === id ? { ...i, ...data } : i)));
    },
    deleteInvoice: async (id) => {
      await wsApi(`invoice/${id}`, "DELETE");
      setInvoices((prev) => prev.filter((i) => i.id !== id));
    },
    generateMonthlyInvoices: async (data) => {
      const res = await wsApi("invoice/generate-monthly", "POST", data);
      await syncWorkspace();
      return res;
    },
    checkChildInOut: async (childId, direction) => {
      const doc = await wsApi("checkin", "POST", { childId, direction });
      setCheckins((prev) => [doc, ...prev]);
    },
    scheduleMeeting: async (data) => {
      const doc = await wsApi("meeting", "POST", data);
      setMeetings((prev) => [...prev, doc]);
    },
    sendStaffMessage: async (text, toEmail = "staff", childId = null) => {
      const doc = await wsApi("message", "POST", { text, toEmail, childId });
      setMessages((prev) => [...prev, doc]);
    },
    inviteMember: async (name, email, role, childIds = [], alsoCaregiver = false) => {
      return wsApi("invite-member", "POST", { name, email, role, childIds, also_caregiver: alsoCaregiver });
    },
    inviteCaregiver: async (name, email) => {
      return wsApi("invite-caregiver", "POST", { name, email });
    },
  };

  const addProfile = (profileData) => {
    const newProfile = { ...profileData, id: `usr_${Date.now()}`, organizationId: "org_prod" };
    setProfiles((prev) => [...prev, newProfile]);
    if (currentUser?.real) wsApi("profile", "POST", newProfile).catch(() => {});
  };

  const addChild = (childData) => {
    const newChild = { ...childData, id: `ch_${Date.now()}`, organizationId: "org_prod" };
    setChildrenList((prev) => [...prev, newChild]);
    if (currentUser?.real) wsApi("child", "POST", newChild).catch(() => {});
  };

  const saveOwnerOnboarding = (data) => {
    setOwnerData(data);
    if (currentUser?.real) wsApi("owner", "PUT", data).catch(() => {});
    setAlerts((prev) => [
      {
        id: `alt_${Date.now()}`,
        type: "success",
        message: "Facility licensing details successfully registered.",
        timestamp: "Just now",
      },
      ...prev,
    ]);
  };

  const dismissAlert = (id) => setAlerts((prev) => prev.filter((a) => a.id !== id));

  const updateProfile = (id, data) => {
    setProfiles((prev) => prev.map((p) => (p.id === id ? { ...p, ...data } : p)));
    if (currentUser?.real) wsApi(`profile/${id}`, "PATCH", data).catch(() => {});
    if (currentUser?.profileId === id) {
      setCurrentUser((prev) => ({
        ...prev,
        fullName: data.fullName ?? prev.fullName,
        avatar: data.avatarBase64 !== undefined ? data.avatarBase64 : prev.avatar,
      }));
    }
  };

  const updateChild = (id, data) => {
    setChildrenList((prev) => prev.map((c) => (c.id === id ? { ...c, ...data } : c)));
    if (currentUser?.real) wsApi(`child/${id}`, "PATCH", data).catch(() => {});
  };

  const deleteProfile = (id) => {
    setProfiles((prev) => prev.filter((p) => p.id !== id));
    if (currentUser?.real) wsApi(`profile/${id}`, "DELETE").catch(() => {});
  };
  const deleteChild = (id) => {
    setChildrenList((prev) => prev.filter((c) => c.id !== id));
    if (currentUser?.real) wsApi(`child/${id}`, "DELETE").catch(() => {});
  };

  const updateCurrentUserAvatar = (avatar) => {
    setCurrentUser((prev) => (prev ? { ...prev, avatar } : prev));
    if (currentUser?.profileId) {
      setProfiles((prev) => prev.map((p) => (p.id === currentUser.profileId ? { ...p, avatarBase64: avatar } : p)));
    }
    if (currentUser?.real) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me/avatar`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ avatar: avatar || "" }),
      })
        .then(async (r) => {
          if (!r.ok) {
            const body = await r.json().catch(() => ({}));
            throw new Error(typeof body.detail === "string" ? body.detail : "Avatar save failed");
          }
          toast.success("Avatar saved.");
        })
        .catch((err) => toast.error(err.message));
    }
  };

  const refreshFromStorage = () => {
    syncWorkspace(); // refetch from the server — persistence hooks keep localStorage in sync
  };

  const signIn = (account) => setCurrentUser(account);
  const signOut = () => {
    setCurrentUser(null);
    ["nt_profiles", "nt_children", "nt_invoices", "nt_owner", "nt_alerts", "nt_rooms"].forEach((k) => localStorage.removeItem(k));
    sessionStorage.removeItem("nt_view");
  };
  const pushAlert = (a) => {
    setAlerts((prev) => [{ id: `alt_${Date.now()}`, type: a.type || "info", message: a.message, timestamp: "Just now" }, ...prev]);
    if (currentUser?.real) wsApi("alert", "POST", a).catch(() => {});
  };

  return (
    <AppContext.Provider
      value={{
        isProductionMode,
        theme,
        ownerData,
        profiles,
        children: childrenList,
        invoices,
        alerts,
        toggleProductionMode,
        toggleTheme,
        applyTheme,
        addProfile,
        addChild,
        saveOwnerOnboarding,
        dismissAlert,
        currentUser,
        accountsDeleted,
        signIn,
        signOut,
        pushAlert,
        updateProfile,
        updateChild,
        updateCurrentUserAvatar,
        deleteProfile,
        deleteChild,
        refreshFromStorage,
        rooms,
        checkins,
        meetings,
        messages,
        wsActions,
        syncWorkspace,
      }}
    >
      <Toaster position="top-center" richColors duration={4000} />
      <div className={theme === "dark" ? "dark" : ""}>
        <div className="min-h-screen bg-[#FFF8E7] text-[#374151] transition-colors duration-300 dark:bg-[#1F2937] dark:text-slate-100">
          {children}
        </div>
      </div>
    </AppContext.Provider>
  );
};

const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error("useApp must be used within an AppProvider");
  return context;
};

// ==========================================
// SHARED INPUT STYLES
// ==========================================

const inputCls =
  "w-full bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl px-4 py-2.5 text-sm text-[#374151] placeholder:text-[#9CA3AF] outline-none focus:ring-2 focus:ring-[#B3E0F2] focus:border-transparent transition-all duration-200 dark:bg-[#273244] dark:border-[#374457] dark:text-slate-100 dark:placeholder:text-slate-500";

const labelCls = "text-xs font-semibold uppercase tracking-wider text-[#9CA3AF] dark:text-slate-400 block mb-1.5";

// ==========================================
// REUSABLE BASE64 FILE UPLOADER
// ==========================================

const Base64FileUpload = ({ label, accept = "image/*", value, onChange, isAvatar = false, testId }) => {
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => onChange(reader.result);
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <label className={labelCls}>{label}</label>
      <div className="flex items-center gap-4 flex-wrap">
        {isAvatar && (
          <div className="w-14 h-14 rounded-full bg-[#FCE6E9] border-2 border-white shadow-sm flex items-center justify-center overflow-hidden shrink-0 dark:bg-[#273244] dark:border-[#374457]">
            {value ? (
              <img src={value} alt="Avatar preview" className="w-full h-full object-cover" />
            ) : (
              <Camera className="w-5 h-5 text-[#E5A0BC] dark:text-slate-500" />
            )}
          </div>
        )}
        <label
          data-testid={`${testId}-upload-btn`}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white hover:bg-[#E6F2FA] border border-[#E6F2FA] rounded-full cursor-pointer text-xs font-semibold text-[#374151] transition-all duration-200 hover:-translate-y-0.5 shadow-sm dark:bg-[#273244] dark:border-[#374457] dark:text-slate-200 dark:hover:bg-[#2f3c50]"
        >
          <Upload className="w-4 h-4 text-[#7CC3E5]" />
          {value ? "Change File" : "Upload File"}
          <input type="file" accept={accept} onChange={handleFile} className="hidden" />
        </label>
        {value && !isAvatar && (
          <span className="text-xs text-[#4A9E6B] flex items-center gap-1 font-medium" data-testid={`${testId}-attached`}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Attached
          </span>
        )}
        {value && (
          <button
            type="button"
            data-testid={`${testId}-remove-btn`}
            onClick={() => onChange(null)}
            className="text-xs text-rose-400 hover:text-rose-500 hover:underline font-medium"
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
};

// ==========================================
// ONBOARDING MODALS & FORMS
// ==========================================

const ModalShell = ({ children, onClose, testId }) => {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 bg-[#374151]/30 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in"
      data-testid={testId}
      onClick={onClose}
    >
      <div
        className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-lg w-full p-8 shadow-xl shadow-blue-900/5 animate-modal-in max-h-[90vh] overflow-y-auto dark:bg-[#232D3D] dark:border-[#374457]"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};

const DaycareOnboardingModal = ({ isOpen, onClose }) => {
  const { saveOwnerOnboarding, ownerData } = useApp();
  const [form, setForm] = useState({
    facilityName: ownerData?.facilityName || "",
    licenseNumber: ownerData?.licenseNumber || "",
    jurisdiction: ownerData?.jurisdiction || "",
    taxId: ownerData?.taxId || "",
    maxCapacity: ownerData?.maxCapacity || 50,
    address: ownerData?.address || "",
    phone: ownerData?.phone || "",
    active_for_enrollment: ownerData?.active_for_enrollment || false,
    licenseDocumentBase64: null,
    isVerified: false,
  });

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    saveOwnerOnboarding({ ...form, isVerified: true });
    onClose();
  };

  return (
    <ModalShell onClose={onClose} testId="facility-onboarding-modal">
      <div className="flex justify-between items-center mb-6 border-b border-[#FCE6E9] dark:border-[#374457] pb-4">
        <h2 className="text-lg font-extrabold text-[#374151] dark:text-slate-100 flex items-center gap-2 font-heading">
          <span className="w-9 h-9 rounded-full bg-[#E6F2FA] flex items-center justify-center">
            <Building2 className="w-5 h-5 text-[#5CB3DC]" />
          </span>
          Facility Licensing & Setup
        </h2>
        <button
          onClick={onClose}
          data-testid="facility-modal-close-btn"
          className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors dark:hover:bg-[#2f3c50]"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className={labelCls}>Facility / Trade Name</label>
          <input
            required
            type="text"
            data-testid="facility-name-input"
            value={form.facilityName}
            onChange={(e) => setForm({ ...form, facilityName: e.target.value })}
            className={inputCls}
            placeholder="e.g. NestTrack Early Education Center"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>State License #</label>
            <input
              required
              type="text"
              data-testid="license-number-input"
              value={form.licenseNumber}
              onChange={(e) => setForm({ ...form, licenseNumber: e.target.value })}
              className={inputCls}
              placeholder="LIC-99201-X"
            />
          </div>
          <div>
            <label className={labelCls}>Jurisdiction / Board</label>
            <input
              required
              type="text"
              data-testid="jurisdiction-input"
              value={form.jurisdiction}
              onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })}
              className={inputCls}
              placeholder="Dept. of Health"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Tax ID / EIN</label>
            <input
              required
              type="text"
              data-testid="tax-id-input"
              value={form.taxId}
              onChange={(e) => setForm({ ...form, taxId: e.target.value })}
              className={inputCls}
              placeholder="XX-XXXXXXX"
            />
          </div>
          <div>
            <label className={labelCls}>Max Capacity</label>
            <input
              required
              type="number"
              data-testid="max-capacity-input"
              value={form.maxCapacity}
              onChange={(e) => setForm({ ...form, maxCapacity: parseInt(e.target.value) || 0 })}
              className={inputCls}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Street Address</label>
            <input
              type="text"
              data-testid="facility-address-input"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className={inputCls}
              placeholder="123 Main St, City, ST"
            />
          </div>
          <div>
            <label className={labelCls}>Contact Phone</label>
            <input
              type="tel"
              data-testid="facility-phone-input"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className={inputCls}
              placeholder="(555) 123-4567"
            />
          </div>
        </div>
        <label className="flex items-center gap-2.5 bg-[#E2F4E1]/70 border border-[#C4E5C2] rounded-2xl px-4 py-3 cursor-pointer" data-testid="facility-enroll-toggle-label">
          <input
            type="checkbox"
            checked={form.active_for_enrollment}
            onChange={(e) => setForm({ ...form, active_for_enrollment: e.target.checked })}
            data-testid="facility-enroll-toggle"
            className="w-4 h-4 accent-[#7CC98F]"
          />
          <span className="text-xs font-bold text-[#2F6031]">
            Active for Enrollment — list this facility in the parent enrollment directory (public search). Uncheck to go Inactive.
          </span>
        </label>
        <Base64FileUpload
          label="Upload Proof of Licensing (PDF or Image)"
          accept="image/*,application/pdf"
          value={form.licenseDocumentBase64}
          onChange={(base64) => setForm({ ...form, licenseDocumentBase64: base64 })}
          testId="license-doc-upload"
        />
        <div className="pt-4 flex justify-end gap-3 border-t border-[#FCE6E9] dark:border-[#374457]">
          <button
            type="button"
            onClick={onClose}
            data-testid="facility-modal-cancel-btn"
            className="px-5 py-2.5 bg-[#FFF9F2] border border-[#FCE6E9] text-[#6B7280] rounded-full text-sm font-semibold hover:bg-[#FCE6E9] transition-all duration-200 dark:bg-[#273244] dark:border-[#374457] dark:text-slate-300 dark:hover:bg-[#2f3c50]"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="facility-modal-submit-btn"
            className="px-5 py-2.5 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-sm font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-sm shadow-pink-200"
          >
            Submit Certification
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

const MemberEnrollmentModal = ({ isOpen, onClose, defaultTab = "staff" }) => {
  const { addProfile, addChild, currentUser, wsActions } = useApp();
  const [tab, setTab] = useState(defaultTab);

  const [staffForm, setStaffForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    role: "teacher",
    avatarBase64: null,
  });
  const [inviteToCare, setInviteToCare] = useState(false);

  const [childForm, setChildForm] = useState({
    firstName: "",
    lastName: "",
    dob: "",
    classroomId: "cls_toddlers",
    allergies: "",
    medicalNotes: "",
    photoBase64: null,
  });

  if (!isOpen) return null;

  const handleStaffSubmit = (e) => {
    e.preventDefault();
    addProfile({ ...staffForm, isActive: true });
    if (currentUser?.real && staffForm.email) {
      wsActions
        .inviteMember(staffForm.fullName, staffForm.email, staffForm.role === "parent" ? "parent" : "staff", [], inviteToCare)
        .then((res) => {
          if (res?.claim_link) navigator.clipboard?.writeText(res.claim_link).catch(() => {});
          const base = inviteToCare
            ? `Invite created for ${staffForm.email} — covers both their account and Care Connections.`
            : `Invite created for ${staffForm.email} — they can claim their account.`;
          const mail =
            res?.email_sent === false
              ? " Email is rate-limited — claim link copied; share it manually."
              : res?.claim_link
                ? " Invite email sent + claim link copied for manual sharing."
                : "";
          toast.success(base + mail);
        })
        .catch((err) => toast.error(err.message));
    }
    setInviteToCare(false);
    onClose();
  };

  const handleChildSubmit = (e) => {
    e.preventDefault();
    addChild({
      ...childForm,
      allergies: childForm.allergies.split(",").map((s) => s.trim()).filter(Boolean),
      emergencyContacts: [],
    });
    onClose();
  };

  return (
    <ModalShell onClose={onClose} testId="member-enrollment-modal">
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2 bg-[#FFF9F2] border border-[#FCE6E9] rounded-full p-1 dark:bg-[#273244] dark:border-[#374457]">
          <button
            onClick={() => setTab("staff")}
            data-testid="enroll-tab-staff"
            className={`text-xs font-bold flex items-center gap-1.5 px-4 py-2 rounded-full transition-all duration-200 ${
              tab === "staff"
                ? "bg-[#F8C8DC] text-[#374151] shadow-sm"
                : "text-[#9CA3AF] hover:text-[#374151] dark:hover:text-slate-200"
            }`}
          >
            <UserPlus className="w-3.5 h-3.5" /> Staff / Parent
          </button>
          <button
            onClick={() => setTab("child")}
            data-testid="enroll-tab-child"
            className={`text-xs font-bold flex items-center gap-1.5 px-4 py-2 rounded-full transition-all duration-200 ${
              tab === "child"
                ? "bg-[#B3E0F2] text-[#374151] shadow-sm"
                : "text-[#9CA3AF] hover:text-[#374151] dark:hover:text-slate-200"
            }`}
          >
            <Baby className="w-3.5 h-3.5" /> Child
          </button>
        </div>
        <button
          onClick={onClose}
          data-testid="enroll-modal-close-btn"
          className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors dark:hover:bg-[#2f3c50]"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {tab === "staff" ? (
        <form onSubmit={handleStaffSubmit} className="space-y-4" data-testid="staff-enroll-form">
          <Base64FileUpload
            label="Profile Avatar Photo"
            isAvatar
            value={staffForm.avatarBase64}
            onChange={(base64) => setStaffForm({ ...staffForm, avatarBase64: base64 })}
            testId="staff-avatar-upload"
          />
          <div>
            <label className={labelCls}>Full Name</label>
            <input
              required
              type="text"
              data-testid="staff-name-input"
              value={staffForm.fullName}
              onChange={(e) => setStaffForm({ ...staffForm, fullName: e.target.value })}
              className={inputCls}
              placeholder="e.g. Amelia Rose"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Email Address</label>
              <input
                required
                type="email"
                data-testid="staff-email-input"
                value={staffForm.email}
                onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                className={inputCls}
                placeholder="name@daycare.com"
              />
            </div>
            <div>
              <label className={labelCls}>Role Assignment</label>
              <select
                value={staffForm.role}
                data-testid="staff-role-select"
                onChange={(e) => setStaffForm({ ...staffForm, role: e.target.value })}
                className={inputCls}
              >
                <option value="teacher">Teacher / Staff</option>
                <option value="parent">Parent / Guardian</option>
                <option value="admin">Administrator</option>
              </select>
            </div>
          </div>
          {currentUser?.real && (
            <label className="flex items-center gap-2.5 bg-[#E6F2FA]/60 border border-[#B3E0F2] rounded-2xl px-4 py-3 cursor-pointer dark:bg-[#273244] dark:border-[#374457]" data-testid="staff-invite-care-label">
              <input
                type="checkbox"
                checked={inviteToCare}
                onChange={(e) => setInviteToCare(e.target.checked)}
                data-testid="staff-invite-care-checkbox"
                className="w-4 h-4 accent-[#E5A0BC]"
              />
              <span className="text-xs font-bold text-[#3D7E9E] dark:text-slate-300">
                Also invite to Care Connections as a caregiver — they'll get a separate claim link to publish a marketplace profile.
              </span>
            </label>
          )}
          <div className="pt-4 flex justify-end gap-3 border-t border-[#FCE6E9] dark:border-[#374457]">
            <button
              type="button"
              onClick={onClose}
              data-testid="staff-form-cancel-btn"
              className="px-5 py-2.5 bg-[#FFF9F2] border border-[#FCE6E9] text-[#6B7280] rounded-full text-sm font-semibold hover:bg-[#FCE6E9] transition-all duration-200 dark:bg-[#273244] dark:border-[#374457] dark:text-slate-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="staff-form-submit-btn"
              className="px-5 py-2.5 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-sm font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-sm shadow-pink-200"
            >
              Save Profile
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleChildSubmit} className="space-y-4" data-testid="child-enroll-form">
          <Base64FileUpload
            label="Child Photo"
            isAvatar
            value={childForm.photoBase64}
            onChange={(base64) => setChildForm({ ...childForm, photoBase64: base64 })}
            testId="child-photo-upload"
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>First Name</label>
              <input
                required
                type="text"
                data-testid="child-first-name-input"
                value={childForm.firstName}
                onChange={(e) => setChildForm({ ...childForm, firstName: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Last Name</label>
              <input
                required
                type="text"
                data-testid="child-last-name-input"
                value={childForm.lastName}
                onChange={(e) => setChildForm({ ...childForm, lastName: e.target.value })}
                className={inputCls}
              />
            </div>
          </div>
          <div>
            <label className={labelCls}>Date of Birth</label>
            <input
              required
              type="date"
              data-testid="child-dob-input"
              value={childForm.dob}
              onChange={(e) => setChildForm({ ...childForm, dob: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Known Allergies (Comma separated)</label>
            <input
              type="text"
              data-testid="child-allergies-input"
              placeholder="Peanuts, Shellfish, Latex"
              value={childForm.allergies}
              onChange={(e) => setChildForm({ ...childForm, allergies: e.target.value })}
              className={inputCls}
            />
          </div>
          <div className="pt-4 flex justify-end gap-3 border-t border-[#FCE6E9] dark:border-[#374457]">
            <button
              type="button"
              onClick={onClose}
              data-testid="child-form-cancel-btn"
              className="px-5 py-2.5 bg-[#FFF9F2] border border-[#FCE6E9] text-[#6B7280] rounded-full text-sm font-semibold hover:bg-[#FCE6E9] transition-all duration-200 dark:bg-[#273244] dark:border-[#374457] dark:text-slate-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="child-form-submit-btn"
              className="px-5 py-2.5 bg-[#B3E0F2] hover:bg-[#9ACCE0] text-[#374151] rounded-full text-sm font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-sm shadow-blue-200"
            >
              Complete Enrollment
            </button>
          </div>
        </form>
      )}
    </ModalShell>
  );
};

// ==========================================
// PROFILE / CHILD EDIT MODALS
// ==========================================

const EditProfileModal = ({ profile, onClose }) => {
  const { updateProfile } = useApp();
  const [form, setForm] = useState({
    fullName: profile.fullName,
    email: profile.email,
    phone: profile.phone,
    role: profile.role,
    avatarBase64: profile.avatarBase64,
    socials: profile.socials || {},
  });

  const submit = (e) => {
    e.preventDefault();
    updateProfile(profile.id, form);
    onClose();
  };

  return (
    <ModalShell onClose={onClose} testId="edit-profile-modal">
      <div className="flex justify-between items-center mb-6 border-b border-[#FCE6E9] dark:border-[#374457] pb-4">
        <h2 className="text-lg font-extrabold text-[#374151] dark:text-slate-100 flex items-center gap-2 font-heading">
          <span className="w-9 h-9 rounded-full bg-[#FCE6E9] flex items-center justify-center">
            <Pencil className="w-4 h-4 text-[#E5A0BC]" />
          </span>
          Edit Profile — {profile.fullName}
        </h2>
        <button onClick={onClose} data-testid="edit-profile-close" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <Base64FileUpload
          label="Avatar Photo"
          isAvatar
          value={form.avatarBase64}
          onChange={(b64) => setForm({ ...form, avatarBase64: b64 })}
          testId="edit-profile-avatar"
        />
        <div>
          <label className={labelCls}>Full Name</label>
          <input required type="text" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} className={inputCls} data-testid="edit-profile-name" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Email</label>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="edit-profile-email" />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="edit-profile-phone" />
          </div>
        </div>
        <div>
          <label className={labelCls}>Role</label>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} data-testid="edit-profile-role">
            <option value="teacher">Teacher / Staff</option>
            <option value="parent">Parent / Guardian</option>
            <option value="admin">Administrator</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Social Links (optional)</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SOCIAL_PLATFORMS.map((s) => (
              <input
                key={s.key}
                type="url"
                value={form.socials[s.key] || ""}
                onChange={(e) => setForm({ ...form, socials: { ...form.socials, [s.key]: e.target.value } })}
                className={inputCls}
                placeholder={s.placeholder}
                data-testid={`edit-profile-social-${s.key}`}
              />
            ))}
          </div>
        </div>
        <div className="pt-4 flex justify-end gap-3 border-t border-[#FCE6E9] dark:border-[#374457]">
          <button type="button" onClick={onClose} className="px-5 py-2.5 bg-[#FFF9F2] border border-[#FCE6E9] text-[#6B7280] rounded-full text-sm font-semibold hover:bg-[#FCE6E9] transition-all dark:bg-[#273244] dark:border-[#374457] dark:text-slate-300" data-testid="edit-profile-cancel">
            Cancel
          </button>
          <button type="submit" className="px-5 py-2.5 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-sm font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-sm shadow-pink-200" data-testid="edit-profile-submit">
            Save Changes
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

const EditChildModal = ({ child, onClose }) => {
  const { updateChild } = useApp();
  const [form, setForm] = useState({
    firstName: child.firstName,
    lastName: child.lastName,
    dob: child.dob,
    allergies: child.allergies.join(", "),
    photoBase64: child.photoBase64,
  });

  const submit = (e) => {
    e.preventDefault();
    updateChild(child.id, {
      ...form,
      allergies: form.allergies.split(",").map((s) => s.trim()).filter(Boolean),
    });
    onClose();
  };

  return (
    <ModalShell onClose={onClose} testId="edit-child-modal">
      <div className="flex justify-between items-center mb-6 border-b border-[#FCE6E9] dark:border-[#374457] pb-4">
        <h2 className="text-lg font-extrabold text-[#374151] dark:text-slate-100 flex items-center gap-2 font-heading">
          <span className="w-9 h-9 rounded-full bg-[#E6F2FA] flex items-center justify-center">
            <Baby className="w-4 h-4 text-[#5CB3DC]" />
          </span>
          Edit — {child.firstName} {child.lastName}
        </h2>
        <button onClick={onClose} data-testid="edit-child-close" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <Base64FileUpload
          label="Child Photo"
          isAvatar
          value={form.photoBase64}
          onChange={(b64) => setForm({ ...form, photoBase64: b64 })}
          testId="edit-child-photo"
        />
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>First Name</label>
            <input required type="text" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className={inputCls} data-testid="edit-child-first-name" />
          </div>
          <div>
            <label className={labelCls}>Last Name</label>
            <input required type="text" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className={inputCls} data-testid="edit-child-last-name" />
          </div>
        </div>
        <div>
          <label className={labelCls}>Date of Birth</label>
          <input required type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className={inputCls} data-testid="edit-child-dob" />
        </div>
        <div>
          <label className={labelCls}>Allergies (comma separated)</label>
          <input type="text" value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} className={inputCls} data-testid="edit-child-allergies" />
        </div>
        <div className="pt-4 flex justify-end gap-3 border-t border-[#FCE6E9] dark:border-[#374457]">
          <button type="button" onClick={onClose} className="px-5 py-2.5 bg-[#FFF9F2] border border-[#FCE6E9] text-[#6B7280] rounded-full text-sm font-semibold hover:bg-[#FCE6E9] transition-all dark:bg-[#273244] dark:border-[#374457] dark:text-slate-300" data-testid="edit-child-cancel">
            Cancel
          </button>
          <button type="submit" className="px-5 py-2.5 bg-[#B3E0F2] hover:bg-[#9ACCE0] text-[#374151] rounded-full text-sm font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-sm shadow-blue-200" data-testid="edit-child-submit">
            Save Changes
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

// Self-serve role profiles: create or switch between staff / parent / caregiver
// My Live Events — hosts end, pause, resume and go live right from their profile
const MyLivePanel = () => {
  const { currentUser } = useApp();
  const [events, setEvents] = useState(null);
  const [busy, setBusy] = useState(null);
  const canHost = liveLocAllowed(currentUser, "profile") || (events && events.length > 0);
  const load = useCallback(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/live/mine`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setEvents(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [load]);
  const hostRoles = ["staff", "daycare_admin", "admin", "master"];
  const isHost = currentUser && hostRoles.some((r) => [currentUser.role, ...(currentUser.extra_roles || [])].includes(r));
  if (!isHost) return null;
  const act = async (id, action, label) => {
    setBusy(id + action);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/live/${id}/${action}`, { method: "POST", credentials: "include" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Action failed");
      toast.success(label);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="mt-4" data-testid="my-live-panel">
      <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#9CA3AF] mb-2.5">My Live Events — end, pause & resume anytime</p>
      {liveLocAllowed(currentUser, "profile") && (
        <button onClick={goLiveNav} data-testid="profile-go-live" className="w-full mb-3 flex items-center justify-center gap-2 px-4 py-2.5 bg-rose-500 hover:bg-rose-600 text-white rounded-2xl text-xs font-extrabold transition-colors">
          <Video className="w-3.5 h-3.5" /> Go Live Now
        </button>
      )}
      {!events || events.length === 0 ? (
        <p className="text-[11px] text-[#9CA3AF] font-semibold" data-testid="my-live-empty">No active, paused or scheduled live events right now.</p>
      ) : (
        <div className="space-y-2" data-testid="my-live-list">
          {events.map((ev) => (
            <div key={ev.id} className="flex items-center justify-between gap-2 px-3.5 py-2.5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl dark:bg-[#273244] dark:border-[#374457]" data-testid={`my-live-row-${ev.id}`}>
              <div className="min-w-0">
                <p className="text-xs font-extrabold text-[#374151] dark:text-slate-100 truncate flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${ev.status === "live" ? "bg-rose-500 animate-pulse" : ev.status === "paused" ? "bg-amber-400" : "bg-[#5CB3DC]"}`} />
                  {ev.title}
                </p>
                <p className="text-[9px] font-bold uppercase tracking-wide text-[#9CA3AF]">{ev.status}{ev.scheduled_at && ev.status === "scheduled" ? ` · ${new Date(ev.scheduled_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : ""}</p>
              </div>
              <div className="flex gap-1.5 shrink-0">
                {ev.status === "live" && (
                  <>
                    <button onClick={() => act(ev.id, "pause", "Stream paused — viewers see an intermission. Resume anytime.")} disabled={busy} data-testid={`my-live-pause-${ev.id}`} className="px-3 py-1.5 bg-[#FFF2D1] hover:bg-[#FCE9BB] border border-[#F5E3B3] text-[#8a6d1a] rounded-full text-[10px] font-extrabold transition-colors disabled:opacity-50">Pause</button>
                    <button onClick={() => act(ev.id, "end", "Live event ended.")} disabled={busy} data-testid={`my-live-end-${ev.id}`} className="px-3 py-1.5 bg-rose-500 hover:bg-rose-600 text-white rounded-full text-[10px] font-extrabold transition-colors disabled:opacity-50">End</button>
                  </>
                )}
                {ev.status === "paused" && (
                  <button onClick={() => act(ev.id, "resume", "You're back live!")} disabled={busy} data-testid={`my-live-resume-${ev.id}`} className="px-3 py-1.5 bg-[#7CC98F] hover:bg-[#5FB878] text-white rounded-full text-[10px] font-extrabold transition-colors disabled:opacity-50">Resume</button>
                )}
                {ev.status === "scheduled" && (
                  <button onClick={() => act(ev.id, "go-live", "You're live! Everyone with the link can join instantly.")} disabled={busy} data-testid={`my-live-golive-${ev.id}`} className="px-3 py-1.5 bg-rose-500 hover:bg-rose-600 text-white rounded-full text-[10px] font-extrabold transition-colors disabled:opacity-50">Go Live</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ProfileRoles = () => {
  const { currentUser } = useApp();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState("");
  const [active, setActive] = useState(true);
  const B = process.env.REACT_APP_BACKEND_URL;
  const load = () =>
    fetch(`${B}/api/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        setInfo({ role: d.role, extra: d.extra_roles || [], canAddStaff: d.can_add_staff !== false, canAddParent: d.can_add_parent !== false });
        setActive(d.profile_active !== false);
      })
      .catch(() => {});
  useEffect(() => {
    if (currentUser?.real) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.real]);
  if (!info) return null;
  const SWITCHABLE_LABELS = { parent: "Parent", staff: "Staff", caregiver: "Caregiver" };
  const BASE_LABELS = { master: "Admin", daycare_admin: "Daycare Admin" };
  const pretty = (r) => SWITCHABLE_LABELS[r] || BASE_LABELS[r] || r.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const baseKeys = ["parent", "staff", "caregiver"];
  const OPTIONS = [
    ...baseKeys.map((k) => ({ key: k, label: SWITCHABLE_LABELS[k] })),
    ...[info.role, ...info.extra].filter((r) => !baseKeys.includes(r)).filter((r, i, arr) => arr.indexOf(r) === i).map((r) => ({ key: r, label: pretty(r) })),
  ];
  const toggleActive = async () => {
    const next = !active;
    setActive(next);
    try {
      const res = await fetch(`${B}/api/account/active`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active: next }),
      });
      if (!res.ok) throw new Error("Could not update visibility");
      toast.success(next ? "Profile is active — you're visible again." : "Profile set inactive — hidden from public maps and boards.");
    } catch (err) {
      setActive(!next);
      toast.error(err.message);
    }
  };
  const run = async (kind, role) => {
    setBusy(role);
    try {
      const res = await fetch(`${B}/api/account/roles/${kind}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Action failed");
      if (kind === "switch") {
        toast.success(`Switched to your ${role} profile.`);
        window.location.reload();
        return;
      }
      toast.success(`${role.charAt(0).toUpperCase() + role.slice(1)} profile created — tap Switch anytime.`);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="mt-6 pt-5 border-t border-[#FCE6E9] dark:border-[#374457]" data-testid="profile-roles">
      <div className="flex items-center justify-between mb-4 p-3 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl dark:bg-[#273244] dark:border-[#374457]">
        <div>
          <p className="text-xs font-extrabold text-[#374151] dark:text-slate-100">Profile Active</p>
          <p className="text-[10px] text-[#9CA3AF] font-medium">Off hides you from public maps & caregiver boards.</p>
        </div>
        <button
          onClick={toggleActive}
          data-testid="profile-active-toggle"
          title="Toggle profile active"
          className={`w-11 h-6 rounded-full transition-colors relative shrink-0 ${active ? "bg-[#7CC98F]" : "bg-[#D1D5DB]"}`}
        >
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${active ? "left-[22px]" : "left-0.5"}`} />
        </button>
      </div>
      <button
        onClick={async () => {
          try {
            const ok = await enablePush();
            if (ok) toast.success("Push notifications enabled — you'll get new-message alerts even when the app is closed.");
            else toast.error("Notifications were not allowed by the browser.");
          } catch (e) {
            toast.error(e.message || "Could not enable notifications");
          }
        }}
        data-testid="enable-push-btn"
        className="w-full mb-3 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#E6F2FA] hover:bg-[#D6EBF7] border border-[#B3E0F2] text-[#3D7E9E] rounded-2xl text-xs font-extrabold transition-colors"
      >
        <Bell className="w-3.5 h-3.5" /> Enable push notifications
      </button>
      <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#9CA3AF] mb-2.5">My Profiles — create or switch how you use NestTrack</p>
      <div className="grid grid-cols-3 gap-2">
        {OPTIONS.map((o) => {
          const isActive = info.role === o.key;
          const has = isActive || info.extra.includes(o.key);
          const locked = !has && ((o.key === "staff" && !info.canAddStaff) || (o.key === "parent" && !info.canAddParent));
          const lockHint = o.key === "staff" ? "Requires daycare listing" : "Requires enrolled child";
          return (
            <button
              key={o.key}
              disabled={isActive || busy === o.key || locked}
              title={locked ? lockHint : undefined}
              onClick={() => run(has ? "switch" : "add", o.key)}
              data-testid={`profile-role-${o.key}`}
              className={`flex flex-col items-center gap-1 p-3 rounded-2xl border text-xs font-extrabold transition-colors disabled:opacity-60 ${
                isActive
                  ? "bg-[#E2F4E1] border-[#BEE3BD] text-[#2F6031]"
                  : locked
                    ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-[#FFF9F2] border-[#FCE6E9] text-[#374151] hover:bg-[#FCE6E9]/50 dark:bg-[#273244] dark:border-[#374457] dark:text-slate-200"
              }`}
            >
              {o.label}
              <span className={`text-[9px] font-bold uppercase tracking-wide ${isActive ? "text-[#2F6031]" : "text-[#9CA3AF]"}`}>
                {busy === o.key ? "Working…" : isActive ? "Active" : has ? "Switch" : locked ? "Locked" : "Create"}
              </span>
              {locked && <span className="text-[8px] font-semibold text-[#C4C9D1] normal-case tracking-normal">{lockHint}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// In-app live invite popup — safety net when email is rate-limited: invited users get a direct Join Live Now
const LiveInvitePopup = ({ meeting, onJoin, onDismiss }) => (
  <div className="fixed bottom-5 right-5 z-[70] w-80 bg-white border-2 border-rose-200 rounded-3xl shadow-2xl p-5 animate-fade-up dark:bg-[#273244] dark:border-rose-900" data-testid="live-invite-popup">
    <p className="flex items-center gap-2 text-[10px] font-extrabold uppercase tracking-wider text-rose-500 mb-1.5">
      <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" /> Live now
    </p>
    <p className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100">{meeting.title}</p>
    <p className="text-[11px] text-[#6B7280] font-medium mt-0.5 dark:text-slate-400">
      {meeting.organizer_name || meeting.organizer_email} invited you to join this live video.
    </p>
    <div className="flex gap-2 mt-3.5">
      <button onClick={onJoin} className="flex-1 px-4 py-2.5 bg-rose-500 hover:bg-rose-600 text-white rounded-full text-xs font-extrabold transition-all" data-testid="live-invite-join">
        Join Live Now
      </button>
      <button onClick={onDismiss} className="px-4 py-2.5 bg-white border border-[#FCE6E9] text-[#9CA3AF] rounded-full text-xs font-extrabold hover:text-[#374151] transition-colors dark:bg-[#1F2937] dark:border-[#374457]" data-testid="live-invite-dismiss">
        Dismiss
      </button>
    </div>
  </div>
);

const AvatarEditModal = ({ onClose }) => {
  const { currentUser, updateCurrentUserAvatar } = useApp();
  const [avatar, setAvatar] = useState(currentUser?.avatar || null);
  const [myMeetings, setMyMeetings] = useState([]);
  const [liveBusy, setLiveBusy] = useState(false);
  const [publicEvent, setPublicEvent] = useState(false);
  const [livePoster, setLivePoster] = useState("");
  const livePublicOk = currentUser?.position === "owner" || currentUser?.position === "admin" || Boolean(currentUser?.live_public_approved);
  const [lrOpen, setLrOpen] = useState(false);
  const [lrType, setLrType] = useState("");
  const [lrReason, setLrReason] = useState("");
  const [lrBusy, setLrBusy] = useState(false);
  const requestLiveAccess = async (e) => {
    e.preventDefault();
    setLrBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/live-access/request`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event_type: lrType, reason: lrReason }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not send the request");
      toast.success(body.already_pending ? "Your request is already pending review." : body.already_approved ? "You're already approved for public live events." : "Request sent — master admins will review it.");
      setLrOpen(false);
      setLrReason("");
      setLrType("");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLrBusy(false);
    }
  };
  const [provider, setProvider] = useState("jitsi");
  const [liveEmails, setLiveEmails] = useState("");
  const [mtg, setMtg] = useState({ title: "", date: "", time: "", emails: "" });
  const [mtgBusy, setMtgBusy] = useState(false);
  const parseEmails = (raw) => [...new Set(raw.split(/[,\s;]+/).map((e) => e.trim().toLowerCase()).filter((e) => e.includes("@")))];
  const mtgInputCls = "w-full px-3.5 py-2 bg-white border border-[#B3E0F2] rounded-xl text-xs font-semibold text-[#374151] focus:outline-none focus:border-[#5CB3DC]";
  const [invDir, setInvDir] = useState([]);
  const [selUsers, setSelUsers] = useState([]);
  const [selGroups, setSelGroups] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const openPicker = async () => {
    const next = !showPicker;
    setShowPicker(next);
    if (next && invDir.length === 0) {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/invitees`, { credentials: "include" });
        const d = await r.json().catch(() => ({}));
        setInvDir(d.users || []);
      } catch {}
    }
  };

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/mine`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { meetings: [] }))
      .then((d) => setMyMeetings(d.meetings || []))
      .catch(() => {});
  }, []);

  const goLive = async () => {
    setLiveBusy(true);
    try {
      if (publicEvent && currentUser?.role === "master") {
        // Approved admins may start a public homepage live event straight from their profile
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/live/start`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title: `${currentUser.name || "Admin"} — Community Live`, description: "", is_public: true, provider, poster: livePoster }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not start the public event");
        window.dispatchEvent(new CustomEvent("nt-open-public-live", { detail: { eventId: body.id } }));
        toast.success("You're live — the public event is on the homepage now.");
        return;
      }
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/instant`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider, emails: parseEmails(liveEmails), user_ids: selUsers, groups: selGroups }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not start a live room");
      navigator.clipboard.writeText(body.link).catch(() => {});
      if (body.provider === "jitsi") {
        window.open(body.link, "_blank", "noopener");
      } else if (body.roomSlug) {
        openNativeMeeting(body.roomSlug);
      }
      toast.success(`You're live — join link copied${body.invited ? ` and emailed to ${body.invited} guest${body.invited > 1 ? "s" : ""}` : ""}.`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLiveBusy(false);
    }
  };

  const scheduleMeeting = async (e) => {
    e.preventDefault();
    setMtgBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/schedule`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: mtg.title, date: mtg.date, time: mtg.time, provider, emails: parseEmails(mtg.emails), user_ids: selUsers, groups: selGroups }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not schedule the meeting");
      const n = (body.participants || []).length;
      if (body.link) navigator.clipboard?.writeText(body.link).catch(() => {});
      toast.success(`Meeting scheduled — invite${n === 1 ? "" : "s"} emailed to ${n} guest${n === 1 ? "" : "s"}. Join link copied for manual sharing.`);
      setMtg({ title: "", date: "", time: "", emails: "" });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setMtgBusy(false);
    }
  };

  const ackMeeting = (id) => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/${id}/ack`, { method: "POST", credentials: "include" }).catch(() => {});
    setMyMeetings((prev) => prev.filter((x) => x.id !== id));
  };

  return (
    <ModalShell onClose={onClose} testId="avatar-edit-modal">
      <div className="flex justify-between items-center mb-6 border-b border-[#FCE6E9] dark:border-[#374457] pb-4">
        <h2 className="text-lg font-extrabold text-[#374151] dark:text-slate-100 flex items-center gap-2 font-heading">
          <span className="w-9 h-9 rounded-full bg-[#FCE6E9] flex items-center justify-center">
            <Camera className="w-4 h-4 text-[#E5A0BC]" />
          </span>
          My Avatar
        </h2>
        <button onClick={onClose} data-testid="avatar-edit-close" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
      <Base64FileUpload label="Account Avatar" isAvatar value={avatar} onChange={setAvatar} testId="my-avatar-upload" />
      <ProfileRoles />
      <MyLivePanel />

      {/* Live video quick access + scheduled meeting/video alerts + app downloads */}
      <div className="mt-5 p-4 bg-[#E6F2FA]/40 border border-[#B3E0F2] rounded-2xl" data-testid="avatar-live-panel">
        <div className="flex items-center justify-between mb-2.5">
          <p className="flex items-center gap-2 text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wide">
            <Video className="w-3.5 h-3.5 text-[#5CB3DC]" /> Meetings & Live Video
          </p>
          {myMeetings.length > 0 && (
            <span className="px-2 py-0.5 bg-rose-500 text-white text-[9px] font-black rounded-full" data-testid="avatar-meetings-badge">
              {myMeetings.length}
            </span>
          )}
        </div>
        {myMeetings.length > 0 ? (
          <div className="space-y-1.5 mb-2.5" data-testid="avatar-meetings">
            {myMeetings.slice(0, 4).map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-2 p-2.5 bg-white border border-[#B3E0F2] rounded-xl" data-testid={`avatar-meeting-${m.id}`}>
                <div className="min-w-0">
                  <p className="text-[11px] font-extrabold text-[#374151] truncate">{m.title}</p>
                  <p className="text-[10px] text-[#9CA3AF] font-bold">
                    {m.instant && (
                      <span className="inline-flex items-center gap-1 text-rose-500 font-extrabold uppercase">
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" /> Live now ·{" "}
                      </span>
                    )}
                    {m.date} · {m.time} · {m.organizer_email === currentUser?.email ? "you host" : `host: ${m.organizer_name || m.organizer_email}`}
                  </p>
                </div>
                <a
                  href={m.link}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => ackMeeting(m.id)}
                  className="px-2.5 py-1 bg-[#5CB3DC] text-white text-[9px] font-extrabold uppercase rounded-full shrink-0 !no-underline"
                  data-testid={`avatar-meeting-join-${m.id}`}
                >
                  Join
                </a>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[10px] text-[#9CA3AF] font-semibold mb-2.5" data-testid="avatar-meetings-empty">
            No meetings or video calls scheduled with you right now.
          </p>
        )}
        <div className="flex items-center gap-1.5 mb-2.5 flex-wrap" data-testid="avatar-provider-toggle">
          <span className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wide mr-1">Video via</span>
          {[["jitsi", "Jitsi (open)"], ["livekit", "LiveKit (native)"]].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setProvider(key)}
              data-testid={`avatar-provider-${key}`}
              className={`px-3 py-1.5 rounded-full text-[10px] font-extrabold transition-all ${provider === key ? "bg-[#5CB3DC] text-white shadow-sm" : "bg-white border border-[#B3E0F2] text-[#3D7E9E] hover:bg-[#E6F2FA]"}`}
            >
              {label}
            </button>
          ))}
        </div>
        {currentUser?.role === "master" && (livePublicOk ? (
          <label className="flex items-center gap-2 mb-2 cursor-pointer" data-testid="avatar-public-toggle-wrap">
            <input type="checkbox" checked={publicEvent} onChange={(e) => setPublicEvent(e.target.checked)} className="w-3.5 h-3.5 accent-rose-500" data-testid="avatar-public-toggle" />
            <span className="text-[10px] font-bold text-[#374151] dark:text-slate-200">Public community event</span>
          </label>
        ) : (
          <div className="mb-2" data-testid="avatar-live-request-block">
            {!lrOpen ? (
              <button type="button" onClick={() => setLrOpen(true)} className="w-full px-3 py-2 bg-white border border-rose-200 text-rose-500 rounded-xl text-[10px] font-extrabold hover:bg-rose-50 transition-colors" data-testid="avatar-live-request-btn">
                Request Public Live access — tell the master admin what you'll host
              </button>
            ) : (
              <form onSubmit={requestLiveAccess} className="p-2.5 bg-rose-50/60 border border-rose-200 rounded-xl space-y-2" data-testid="avatar-live-request-form">
                <select required value={lrType} onChange={(e) => setLrType(e.target.value)} className={mtgInputCls} data-testid="avatar-live-request-type">
                  <option value="">Event type…</option>
                  {["Networking", "Parent Q&A", "Training / Workshop", "Announcement", "Open House", "Other"].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <textarea required value={lrReason} onChange={(e) => setLrReason(e.target.value)} placeholder="Reason for public viewing (min 10 characters)" rows={2} className={mtgInputCls} data-testid="avatar-live-request-reason" />
                <div className="flex gap-2">
                  <button type="submit" disabled={lrBusy} className="flex-1 px-3 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-full text-[10px] font-extrabold disabled:opacity-50" data-testid="avatar-live-request-submit">
                    {lrBusy ? "Sending…" : "Send request"}
                  </button>
                  <button type="button" onClick={() => setLrOpen(false)} className="px-3 py-2 bg-white border border-[#FCE6E9] text-[#9CA3AF] rounded-full text-[10px] font-extrabold" data-testid="avatar-live-request-cancel">
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        ))}
        {currentUser?.role === "master" && publicEvent && (
          <label className="flex items-center gap-2 mb-2 cursor-pointer px-2.5 py-2 bg-white border border-rose-200 rounded-xl hover:bg-rose-50 transition-colors" data-testid="avatar-poster-upload">
            <Video className="w-3.5 h-3.5 text-rose-400" />
            <span className="text-[10px] font-bold text-[#374151] dark:text-slate-200 flex-1">{livePoster ? "Poster attached — shown as the live preview ✓" : "Add poster image — the preview everyone sees"}</span>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="avatar-poster-input"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (!f) return;
                const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onloadend = () => res(r.result); r.readAsDataURL(f); });
                const img = new Image();
                img.onload = () => {
                  const scale = Math.min(1, 960 / Math.max(img.width, img.height));
                  const c = document.createElement("canvas");
                  c.width = Math.round(img.width * scale);
                  c.height = Math.round(img.height * scale);
                  c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
                  setLivePoster(c.toDataURL("image/jpeg", 0.82));
                };
                img.src = dataUrl;
              }}
            />
          </label>
        )}
        <div className="mb-2 p-2.5 bg-white/60 border border-[#B3E0F2]/60 rounded-2xl" data-testid="avatar-invite-picker">
          <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wide mb-1.5">Invite NestTrack members (applies to Go Live &amp; Schedule)</p>
          <div className="flex gap-1.5 flex-wrap mb-2" data-testid="avatar-invite-groups">
            {[["admins", "All Admins"], ["owners", "Owners / Sub-admins"], ["staff", "Staff"], ["caregivers", "Caregivers"], ["parents", "Parents"], ["everyone", "Everyone"]].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setSelGroups((prev) => (prev.includes(key) ? prev.filter((g) => g !== key) : [...prev, key]))}
                data-testid={`avatar-group-${key}`}
                className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold transition-all ${selGroups.includes(key) ? "bg-[#5CB3DC] text-white shadow-sm" : "bg-white border border-[#B3E0F2] text-[#3D7E9E] hover:bg-[#E6F2FA]"}`}
              >
                {label}
              </button>
            ))}
          </div>
          <button type="button" onClick={openPicker} data-testid="avatar-pick-people-btn" className="text-[10px] font-extrabold text-[#3D7E9E] hover:underline">
            {showPicker ? "Hide people list" : `Pick people individually${selUsers.length ? ` (${selUsers.length} selected)` : ""}`}
          </button>
          {showPicker && (
            <div className="mt-2 max-h-36 overflow-y-auto space-y-1 pr-1" data-testid="avatar-people-list">
              {invDir.length === 0 && <p className="text-[10px] text-[#9CA3AF] font-semibold">Loading people…</p>}
              {invDir.map((u) => (
                <label key={u.id} className="flex items-center gap-2 text-[11px] font-semibold text-[#374151] cursor-pointer" data-testid={`avatar-person-${u.id}`}>
                  <input
                    type="checkbox"
                    checked={selUsers.includes(u.id)}
                    onChange={() => setSelUsers((prev) => (prev.includes(u.id) ? prev.filter((x) => x !== u.id) : [...prev, u.id]))}
                    className="w-3.5 h-3.5 accent-[#5CB3DC]"
                  />
                  {u.name || u.email} <span className="text-[9px] text-[#9CA3AF] font-bold uppercase">{u.role}{u.position ? ` · ${u.position}` : ""}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        <input
          type="text"
          value={liveEmails}
          onChange={(e) => setLiveEmails(e.target.value)}
          placeholder="External guest emails — comma separated (optional)"
          className={`${mtgInputCls} mb-2`}
          data-testid="avatar-live-emails"
        />
        <button onClick={goLive} disabled={liveBusy} data-testid="avatar-golive-btn" className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-rose-500 hover:bg-rose-600 text-white rounded-full text-xs font-extrabold transition-all disabled:opacity-50 mb-2.5">
          <Video className="w-3.5 h-3.5" /> {liveBusy ? "Opening room…" : "Go Live — instant video room"}
        </button>
        <form onSubmit={scheduleMeeting} className="pt-2.5 border-t border-[#B3E0F2]/60 space-y-2 mb-2.5" data-testid="avatar-schedule-form">
          <p className="text-[10px] font-extrabold text-[#3D7E9E] uppercase tracking-wide">Schedule a meeting — invite multiple guests</p>
          <input required type="text" value={mtg.title} onChange={(e) => setMtg({ ...mtg, title: e.target.value })} placeholder="Meeting title" className={mtgInputCls} data-testid="avatar-mtg-title" />
          <div className="grid grid-cols-2 gap-2">
            <input required type="date" value={mtg.date} onChange={(e) => setMtg({ ...mtg, date: e.target.value })} className={mtgInputCls} data-testid="avatar-mtg-date" />
            <input required type="time" value={mtg.time} onChange={(e) => setMtg({ ...mtg, time: e.target.value })} className={mtgInputCls} data-testid="avatar-mtg-time" />
          </div>
          <input type="text" value={mtg.emails} onChange={(e) => setMtg({ ...mtg, emails: e.target.value })} placeholder="External guest emails — comma separated (or use the picker/groups above)" className={mtgInputCls} data-testid="avatar-mtg-emails" />
          <button type="submit" disabled={mtgBusy} className="w-full px-4 py-2 bg-[#5CB3DC] hover:bg-[#4AA3CC] text-white rounded-full text-xs font-extrabold transition-all disabled:opacity-50" data-testid="avatar-mtg-submit">
            {mtgBusy ? "Sending invites…" : "Schedule & email invites"}
          </button>
        </form>
        <div className="grid grid-cols-1 gap-2" data-testid="avatar-app-links">
          <a href="/" target="_blank" rel="noreferrer" data-testid="avatar-download-nesttrack" className="flex items-center justify-center gap-1.5 px-3 py-2 bg-[#FCE6E9] border border-[#F8C8DC] text-[#B04A6B] rounded-full text-[10px] font-extrabold !no-underline hover:bg-[#F8C8DC] transition-colors">
            <Download className="w-3 h-3" /> NestTrack App
          </a>
          <p className="text-[10px] text-[#9CA3AF] font-semibold text-center" data-testid="avatar-video-note">Video calls &amp; live rooms run natively inside NestTrack — no other app needed.</p>
        </div>
      </div>

      <div className="pt-6 flex justify-end gap-3">
        <button onClick={onClose} className="px-5 py-2.5 bg-[#FFF9F2] border border-[#FCE6E9] text-[#6B7280] rounded-full text-sm font-semibold hover:bg-[#FCE6E9] transition-all dark:bg-[#273244] dark:border-[#374457] dark:text-slate-300" data-testid="avatar-edit-cancel">
          Cancel
        </button>
        <button
          onClick={() => {
            updateCurrentUserAvatar(avatar);
            onClose();
          }}
          className="px-5 py-2.5 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-sm font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-sm shadow-pink-200"
          data-testid="avatar-edit-save"
        >
          Save Avatar
        </button>
      </div>
    </ModalShell>
  );
};

// ==========================================
// RECORD DETAIL MODAL (click any card/row)
// ==========================================

const RecordDetailModal = ({ title, image, name, rows, onEdit, onDelete, onClose, testId, contact, socials, shareId }) => {
  const [confirming, setConfirming] = useState(false);
  return (
    <ModalShell onClose={onClose} testId={testId}>
      <div className="flex justify-between items-center mb-5 border-b border-[#FCE6E9] dark:border-[#374457] pb-4">
        <h2 className="text-lg font-extrabold font-heading text-[#374151] dark:text-slate-100">{title}</h2>
        <button onClick={onClose} data-testid={`${testId}-close`} className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex items-center gap-4 mb-5">
        <div className="w-16 h-16 rounded-full bg-white border-2 border-[#FCE6E9] overflow-hidden flex items-center justify-center shrink-0 shadow-sm dark:bg-[#1F2937] dark:border-[#374457]">
          {image ? <img src={image} alt={name} className="w-full h-full object-cover" /> : <span className="text-xl font-black text-[#E5A0BC] font-heading">{name.charAt(0)}</span>}
        </div>
        <p className="text-base font-extrabold font-heading text-[#374151] dark:text-slate-100">{name}</p>
      </div>
      <div className="space-y-2 mb-6">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-4 p-3 bg-[#FFF9F2] border border-[#FCE6E9] rounded-xl text-xs dark:bg-[#273244] dark:border-[#374457]">
            <span className="font-bold text-[#9CA3AF] uppercase tracking-wide">{label}</span>
            <span className="font-semibold text-[#374151] text-right dark:text-slate-200">{value || "—"}</span>
          </div>
        ))}
      </div>
      {contact && (
        <div className="mb-4">
          <ContactButtons phone={contact.phone} email={contact.email} testId={`${testId}-contact`} />
        </div>
      )}
      {socials && Object.values(socials).some(Boolean) && (
        <div className="mb-5">
          <SocialLinks links={socials} testId={`${testId}-social`} />
        </div>
      )}
      {shareId && (
        <button
          onClick={() => {
            navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/share/profile/${shareId}`);
            toast.success("Share link copied — it renders a rich preview card when posted anywhere.");
          }}
          data-testid={`${testId}-share`}
          className="w-full mb-4 px-4 py-2.5 bg-[#E6F2FA] hover:bg-[#D6EBF7] border border-[#B3E0F2] text-[#3D7E9E] rounded-full text-xs font-bold transition-colors"
        >
          Copy Share Link
        </button>
      )}
      <div className="flex justify-between gap-3">
        <button
          onClick={() => (confirming ? onDelete() : setConfirming(true))}
          data-testid={`${testId}-delete`}
          className={`px-4 py-2.5 rounded-full text-xs font-bold transition-all duration-200 ${
            confirming ? "bg-rose-500 text-white hover:bg-rose-600" : "bg-rose-50 border border-rose-200 text-rose-500 hover:bg-rose-100"
          }`}
        >
          {confirming ? "Confirm Delete?" : "Delete"}
        </button>
        <button onClick={onEdit} data-testid={`${testId}-edit`} className="px-5 py-2.5 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-xs font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-sm shadow-pink-200">
          Edit Record
        </button>
      </div>
    </ModalShell>
  );
};

// ==========================================
// CORE LAYOUT & NAVIGATION
// ==========================================

const Header = ({ onOpenOwnerModal, onOpenEnrollModal, onOpenCare, onOpenStore, onHome, onSignOut, onEditAvatar, onMasterDash, onOpenInbox, onFacilityDash, onSiteNav }) => {
  const { isProductionMode, theme, alerts, toggleProductionMode, toggleTheme, dismissAlert, ownerData, currentUser } = useApp();
  const isInboxUser = Boolean(currentUser);
  const [msgAlerts, setMsgAlerts] = useState([]);
  useEffect(() => {
    if (!isInboxUser) return;
    const fetchBell = () =>
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/master/bell`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : { messages: [] }))
        .then((d) => setMsgAlerts(d.messages || []))
        .catch(() => {});
    fetchBell();
    const t = setInterval(fetchBell, 30000);
    return () => clearInterval(t);
  }, [isInboxUser]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const mobileItemCls = "flex items-center gap-3 w-full px-4 py-3 rounded-2xl text-sm font-bold text-[#374151] dark:text-slate-200 hover:bg-[#FFF4F7] dark:hover:bg-[#2f3c50] transition-colors text-left";
  const canHostLive = liveLocAllowed(currentUser, "header");
  const [seenAlerts, setSeenAlerts] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("nt_alerts_seen") || "[]");
    } catch {
      return [];
    }
  });
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  useEffect(() => {
    if (!currentUser?.real) return;
    let alive = true;
    const tick = async () => {
      const n = await fetchUnreadCount();
      if (alive) {
        setUnreadMsgs(n);
        syncAppBadge(n);
      }
    };
    tick();
    const t = setInterval(tick, 3000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [currentUser?.real]);
  const alertsRef = useRef(null);

  useEffect(() => {
    if (!showAlerts) return;
    const handleClick = (e) => {
      if (alertsRef.current && !alertsRef.current.contains(e.target)) setShowAlerts(false);
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setShowAlerts(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [showAlerts]);

  const alertDotColor = { warning: "bg-amber-400", info: "bg-[#7CC3E5]", success: "bg-[#7CC98F]", danger: "bg-rose-400" };

  return (
    <header className="border-b border-[#FCE6E9] bg-white/80 backdrop-blur-xl px-3 sm:px-6 py-3.5 flex items-center justify-between gap-2 sticky top-0 z-40 dark:bg-[#232D3D]/85 dark:border-[#374457]">
      <div className="flex items-center gap-5 shrink-0">
        <button className="flex items-center gap-2.5" data-testid="brand-logo" onClick={onHome} title="Back to home">
          <div className="w-10 h-10 rounded-full bg-white border-2 border-[#FCE6E9] shadow-sm overflow-hidden shrink-0 flex items-center justify-center">
            <img src="/assets/logo.png" alt="NestTrack bird logo" className="w-full h-full object-cover" />
          </div>
          <span className="font-black text-xl tracking-tight font-heading text-[#374151] dark:text-slate-100">
            Nest<span className="text-[#E5A0BC]">Track</span>
          </span>
        </button>
        <div
          className="hidden lg:flex items-center gap-2 bg-[#FFF9F2] border border-[#FCE6E9] rounded-full px-3.5 py-1.5 text-xs dark:bg-[#273244] dark:border-[#374457]"
          data-testid="workspace-mode-badge"
        >
          <span className={`w-2 h-2 rounded-full ${isProductionMode ? "bg-amber-400 animate-pulse" : "bg-[#7CC98F]"}`} />
          <span className="text-[#6B7280] font-semibold dark:text-slate-300">
            {isProductionMode ? "Live — Production" : "Demo Workspace"}
          </span>
        </div>
      </div>

      {onSiteNav && (
        <div className="hidden md:block flex-1 max-w-md mx-3 min-w-0">
          <SiteSearch currentUser={currentUser} onNavigate={onSiteNav} testid="site-search" />
        </div>
      )}

      {/* Mobile controls — chat, avatar, hamburger; nav links stack neatly in the dropdown menu */}
      <div className="md:hidden flex items-center gap-2" data-testid="header-mobile-controls">
        {currentUser && (
          <button
            onClick={onOpenCare}
            data-testid="chat-nav-btn-mobile"
            title="Messages"
            className="w-9 h-9 flex items-center justify-center text-[#9CA3AF] hover:text-[#374151] rounded-full border border-[#FCE6E9] bg-white hover:bg-[#FFF9F2] transition-colors relative dark:bg-[#273244] dark:border-[#374457]"
          >
            <MessageCircle className="w-4 h-4" />
            {unreadMsgs > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-400 text-white text-[10px] font-black flex items-center justify-center ring-2 ring-white dark:ring-[#273244]" data-testid="chat-unread-badge-mobile">
                {unreadMsgs}
              </span>
            )}
          </button>
        )}
        {currentUser && (
          <button
            onClick={onEditAvatar}
            data-testid="user-avatar-btn-mobile"
            title="Change my avatar"
            className="w-8 h-8 rounded-full bg-white border-2 border-[#FCE6E9] overflow-hidden flex items-center justify-center shrink-0 shadow-sm hover:border-[#E5A0BC] transition-colors dark:bg-[#1F2937] dark:border-[#374457]"
          >
            {currentUser.avatar ? (
              <img src={currentUser.avatar} alt={currentUser.fullName} className="w-full h-full object-cover" />
            ) : (
              <span className="text-xs font-black text-[#E5A0BC] font-heading">{currentUser.fullName.charAt(0)}</span>
            )}
          </button>
        )}
        <button
          onClick={() => setMenuOpen((o) => !o)}
          data-testid="header-menu-toggle"
          title="Menu"
          className="w-9 h-9 flex items-center justify-center text-[#374151] rounded-full border border-[#FCE6E9] bg-white hover:bg-[#FFF9F2] transition-colors dark:bg-[#273244] dark:border-[#374457] dark:text-slate-200"
        >
          {menuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        </button>
      </div>

      {/* Mobile stacked menu — every workspace link, one clean column */}
      {menuOpen && (
        <div
          className="md:hidden absolute left-0 right-0 top-full border-b border-[#FCE6E9] bg-white/95 backdrop-blur-md shadow-xl px-4 pt-3 pb-4 space-y-1.5 dark:bg-[#232D3D]/95 dark:border-[#374457]"
          data-testid="header-mobile-menu"
        >
          {onSiteNav && (
            <div className="pb-1.5">
              <SiteSearch currentUser={currentUser} onNavigate={(s) => { setMenuOpen(false); onSiteNav(s); }} testid="site-search-mobile" />
            </div>
          )}
          {currentUser && (
            <div className="flex items-center gap-3 px-4 py-2.5 rounded-2xl bg-[#FFF9F2] border border-[#FCE6E9] dark:bg-[#273244] dark:border-[#374457]" data-testid="mobile-menu-user">
              <span className="w-9 h-9 rounded-full bg-white border-2 border-[#FCE6E9] overflow-hidden flex items-center justify-center shrink-0 dark:bg-[#1F2937] dark:border-[#374457]">
                {currentUser.avatar ? (
                  <img src={currentUser.avatar} alt={currentUser.fullName} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-sm font-black text-[#E5A0BC] font-heading">{currentUser.fullName.charAt(0)}</span>
                )}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-extrabold text-[#374151] dark:text-slate-100 truncate">{currentUser.fullName}</p>
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#9CA3AF]">{currentUser.role.replace(/_/g, " ")}</p>
              </div>
            </div>
          )}
          <button onClick={() => { setMenuOpen(false); onOpenCare(); }} className={mobileItemCls} data-testid="mobile-nav-care">
            <HeartHandshake className="w-4 h-4 text-[#E5A0BC]" /> Care Connections
          </button>
          <button onClick={() => { setMenuOpen(false); onOpenStore(); }} className={mobileItemCls} data-testid="mobile-nav-store">
            <ShoppingBag className="w-4 h-4 text-[#5CB3DC]" /> Store
          </button>
          {canHostLive && (
            <button onClick={() => { setMenuOpen(false); goLiveNav(); }} className={`${mobileItemCls} bg-rose-50 text-rose-500 hover:bg-rose-100`} data-testid="mobile-nav-golive">
              <Video className="w-4 h-4" /> Go Live
            </button>
          )}
          {isInboxUser && (
            <button onClick={() => { setMenuOpen(false); onOpenInbox(); }} className={mobileItemCls} data-testid="mobile-nav-inbox">
              <Lock className="w-4 h-4 text-[#5CB3DC]" /> Secure Inbox
            </button>
          )}
          {currentUser?.role === "daycare_admin" && onFacilityDash && (
            <button onClick={() => { setMenuOpen(false); onFacilityDash(); }} className={mobileItemCls} data-testid="mobile-nav-facility">
              <Building2 className="w-4 h-4 text-[#8a6d1a]" /> Facility Dashboard
            </button>
          )}
          {(currentUser?.role === "master" || (currentUser?.extra_roles || []).includes("master")) && onMasterDash && (
            <button onClick={() => { setMenuOpen(false); onMasterDash(); }} className={mobileItemCls} data-testid="mobile-nav-master">
              <ShieldCheck className="w-4 h-4 text-[#374151]" /> Master Dashboard
            </button>
          )}
          {["admin", "master"].includes(currentUser?.role) && (
            <button onClick={() => { setMenuOpen(false); onOpenOwnerModal(); }} className={mobileItemCls} data-testid="mobile-nav-facility-setup">
              <Building2 className="w-4 h-4 text-[#5CB3DC]" /> {ownerData ? "License Details" : "Setup Facility"}
            </button>
          )}
          {currentUser?.role !== "parent" && (
            <button onClick={() => { setMenuOpen(false); onOpenEnrollModal(); }} className={`${mobileItemCls} bg-[#F8C8DC] hover:bg-[#EAAFC5]`} data-testid="mobile-nav-enroll">
              <Plus className="w-4 h-4" /> Enroll New
            </button>
          )}
          {currentUser && (
            <button onClick={() => { setMenuOpen(false); onEditAvatar(); }} className={mobileItemCls} data-testid="mobile-nav-avatar">
              <Camera className="w-4 h-4 text-[#9CA3AF]" /> Edit My Photo
            </button>
          )}
          <button onClick={toggleTheme} className={mobileItemCls} data-testid="mobile-nav-theme">
            {theme === "dark" ? <Sun className="w-4 h-4 text-[#9CA3AF]" /> : <Moon className="w-4 h-4 text-[#9CA3AF]" />} {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </button>
          {currentUser && (
            <button onClick={() => { setMenuOpen(false); onSignOut(); }} className={`${mobileItemCls} text-rose-500 hover:bg-rose-50`} data-testid="mobile-nav-signout">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          )}
        </div>
      )}

      <div className="hidden md:flex items-center gap-2.5 [&>*]:shrink-0 min-w-0" data-testid="header-nav-pills">
        {(currentUser?.role === "master" || (currentUser?.extra_roles || []).includes("master")) && onMasterDash && (
          <button
            onClick={onMasterDash}
            data-testid="master-dash-nav-btn"
            title="Back to Master Dashboard"
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-[#374151] hover:bg-[#1F2937] border border-[#374151] rounded-full text-xs font-bold text-white whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5 shadow-sm"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Master Dashboard</span>
          </button>
        )}
        {currentUser?.role === "daycare_admin" && onFacilityDash && (
          <button
            onClick={onFacilityDash}
            data-testid="facility-dash-nav-btn"
            title="Your facility dashboard — onboarding, staff, monitoring & private analytics"
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-[#FFF2D1] hover:bg-[#FCEABB] border border-[#F5E3B3] rounded-full text-xs font-bold text-[#8a6d1a] whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5 shadow-sm"
          >
            <Building2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Facility Dashboard</span>
          </button>
        )}
        {currentUser && onOpenInbox && (
          <button
            onClick={onOpenInbox}
            data-testid="secure-inbox-nav-btn"
            title="Admin secure inbox — threads, private notes & escalations"
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-white hover:bg-[#E6F2FA] border border-[#B3E0F2] rounded-full text-xs font-bold text-[#374151] whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5 shadow-sm dark:bg-[#273244] dark:border-[#374457] dark:text-slate-200"
          >
            <Lock className="w-3.5 h-3.5 text-[#5CB3DC]" />
            <span className="hidden sm:inline">Secure Inbox</span>
          </button>
        )}
        <button
          onClick={onOpenCare}
          data-testid="care-connections-nav-btn"
          className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-white hover:bg-[#FCE6E9] border border-[#F8C8DC] rounded-full text-xs font-bold text-[#374151] whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5 shadow-sm dark:bg-[#273244] dark:border-[#4a3a4a] dark:text-slate-200"
        >
          <HeartHandshake className="w-3.5 h-3.5 text-[#E5A0BC]" />
          <span className="hidden sm:inline">Care Connections</span>
        </button>

        <button
          onClick={onOpenStore}
          data-testid="store-nav-btn"
          className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-white hover:bg-[#E6F2FA] border border-[#B3E0F2] rounded-full text-xs font-bold text-[#374151] whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5 shadow-sm dark:bg-[#273244] dark:border-[#374457] dark:text-slate-200"
        >
          <ShoppingBag className="w-3.5 h-3.5 text-[#5CB3DC]" />
          <span className="hidden sm:inline">Store</span>
        </button>
        {canHostLive && (
          <button
            onClick={goLiveNav}
            data-testid="go-live-nav-btn"
            title="Go live — opens the Daycare Operations live studio"
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-rose-500 hover:bg-rose-600 rounded-full text-xs font-bold text-white whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5 shadow-sm"
          >
            <Video className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Go Live</span>
          </button>
        )}

        {["admin", "master"].includes(currentUser?.role) && (
          <span
            className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full text-xs font-bold border whitespace-nowrap bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]"
            data-testid="live-badge"
            title="Site is live for real users"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Live</span>
          </span>
        )}

        {["admin", "master"].includes(currentUser?.role) && (
          <button
            onClick={onOpenOwnerModal}
            data-testid="facility-setup-btn"
          title={ownerData ? "License Details" : "Setup Facility"}
          className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-white hover:bg-[#E6F2FA] border border-[#E6F2FA] rounded-full text-xs font-bold text-[#374151] whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5 shadow-sm dark:bg-[#273244] dark:border-[#374457] dark:text-slate-200 dark:hover:bg-[#2f3c50]"
        >
          <Building2 className="w-3.5 h-3.5 text-[#5CB3DC]" />
            <span className="hidden sm:inline">{ownerData ? "License Details" : "Setup Facility"}</span>
          </button>
        )}

        {currentUser?.role !== "parent" && (
          <button
            onClick={onOpenEnrollModal}
            data-testid="enroll-new-btn"
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-xs font-bold whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5 shadow-sm shadow-pink-200"
          >
            <Plus className="w-3.5 h-3.5" />
            Enroll New
          </button>
        )}

        {currentUser && (
          <div className="flex items-center gap-2" data-testid="user-chip">
            <button
              onClick={onEditAvatar}
              data-testid="user-avatar-btn"
              title="Change my avatar"
              className="w-8 h-8 rounded-full bg-white border-2 border-[#FCE6E9] overflow-hidden flex items-center justify-center shrink-0 shadow-sm hover:border-[#E5A0BC] transition-colors dark:bg-[#1F2937] dark:border-[#374457]"
            >
              {currentUser.avatar ? (
                <img src={currentUser.avatar} alt={currentUser.fullName} className="w-full h-full object-cover" />
              ) : (
                <span className="text-xs font-black text-[#E5A0BC] font-heading">{currentUser.fullName.charAt(0)}</span>
              )}
            </button>
            <div className="hidden lg:block">
              <p className="text-xs font-extrabold text-[#374151] dark:text-slate-100 leading-tight">{currentUser.fullName}</p>
              <span
                className={`inline-flex items-center mt-0.5 px-2 py-0.5 rounded-full border text-[9px] font-extrabold uppercase tracking-wide leading-tight ${
                  (currentUser.extra_roles || []).length > 0
                    ? "bg-[#E6F2FA] border-[#B3E0F2] text-[#3D7E9E]"
                    : "bg-[#FFF9F2] border-[#FCE6E9] text-[#9CA3AF]"
                }`}
                data-testid="role-badge"
              >
                {(currentUser.extra_roles || []).length > 0 ? `Viewing as ${currentUser.role.replace(/_/g, " ")}` : currentUser.role.replace(/_/g, " ")}
              </span>
            </div>
            <button
              onClick={onSignOut}
              data-testid="sign-out-btn"
              title="Sign out"
              className="flex items-center gap-1.5 px-3 py-2 bg-white hover:bg-[#FCE6E9] border border-[#FCE6E9] rounded-full text-xs font-bold text-[#6B7280] transition-colors dark:bg-[#273244] dark:border-[#374457] dark:text-slate-300"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        )}

        <button
          onClick={toggleTheme}
          data-testid="theme-toggle-btn"
          className="w-9 h-9 flex items-center justify-center text-[#9CA3AF] hover:text-[#374151] rounded-full border border-[#FCE6E9] bg-white hover:bg-[#FFF9F2] transition-colors dark:bg-[#273244] dark:border-[#374457] dark:hover:bg-[#2f3c50] dark:hover:text-slate-200"
        >
          {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>

        <div className="relative" ref={alertsRef}>
          <button
            onClick={onOpenCare}
            data-testid="chat-nav-btn"
            title="Messages"
            className="w-9 h-9 flex items-center justify-center text-[#9CA3AF] hover:text-[#374151] rounded-full border border-[#FCE6E9] bg-white hover:bg-[#FFF9F2] transition-colors relative dark:bg-[#273244] dark:border-[#374457] dark:hover:bg-[#2f3c50] dark:hover:text-slate-200"
          >
            <MessageCircle className="w-4 h-4" />
            {unreadMsgs > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-400 text-white text-[10px] font-black flex items-center justify-center ring-2 ring-white dark:ring-[#273244]" data-testid="chat-unread-badge">
                {unreadMsgs}
              </span>
            )}
          </button>
          <button
            onClick={() => {
              const next = !showAlerts;
              setShowAlerts(next);
              if (next) {
                const ids = [...new Set([...seenAlerts, ...alerts.map((a) => a.id)])].slice(-200);
                setSeenAlerts(ids);
                localStorage.setItem("nt_alerts_seen", JSON.stringify(ids));
              }
            }}
            data-testid="alerts-bell-btn"
            className="w-9 h-9 flex items-center justify-center text-[#9CA3AF] hover:text-[#374151] rounded-full border border-[#FCE6E9] bg-white hover:bg-[#FFF9F2] transition-colors relative dark:bg-[#273244] dark:border-[#374457] dark:hover:bg-[#2f3c50] dark:hover:text-slate-200"
          >
            <Bell className="w-4 h-4" />
            {alerts.filter((a) => !seenAlerts.includes(a.id)).length + msgAlerts.length > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-400 text-white text-[10px] font-black flex items-center justify-center ring-2 ring-white dark:ring-[#273244]" data-testid="alerts-count">
                {alerts.filter((a) => !seenAlerts.includes(a.id)).length + msgAlerts.length}
              </span>
            )}
          </button>

          {showAlerts && (
            <div
              className="absolute right-0 mt-2 w-80 bg-white border border-[#FCE6E9] rounded-3xl shadow-xl shadow-blue-900/5 p-4 z-50 animate-modal-in dark:bg-[#232D3D] dark:border-[#374457]"
              data-testid="alerts-dropdown"
            >
              <div className="flex justify-between items-center mb-3 border-b border-[#FCE6E9] dark:border-[#374457] pb-2">
                <span className="text-xs font-extrabold uppercase tracking-wider text-[#9CA3AF] font-heading">
                  System Alerts ({alerts.length})
                </span>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                {msgAlerts.length > 0 && (
                  <div data-testid="bell-messages">
                    <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#E5A0BC] mb-1.5">Messages waiting for staff</p>
                    <div className="space-y-1.5 mb-2">
                      {msgAlerts.map((m) => (
                        <button
                          key={m.id}
                          onClick={() => {
                            setShowAlerts(false);
                            if (m.kind === "secure") onOpenInbox();
                            else onOpenCare();
                          }}
                          data-testid={`bell-msg-${m.id}`}
                          className="w-full text-left p-2.5 bg-[#E6F2FA] border border-[#B3E0F2] rounded-2xl hover:bg-[#D8ECF7] transition-colors"
                        >
                          <p className="text-[11px] font-extrabold text-[#374151] dark:text-slate-100">
                            {m.kind === "secure" ? "Secure Inbox" : "Chat"} · {m.title}
                          </p>
                          {m.body && <p className="text-[10px] text-[#6B7280] font-medium line-clamp-1">{m.body}</p>}
                          <p className="text-[9px] font-extrabold text-[#3D7E9E] mt-0.5">Open &amp; respond →</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {alerts.length === 0 ? (
                  <p className="text-xs text-[#9CA3AF] py-3 text-center" data-testid="alerts-empty-state">
                    No active system alerts.
                  </p>
                ) : (
                  alerts.map((alt) => (
                    <div
                      key={alt.id}
                      data-testid={`alert-item-${alt.id}`}
                      className="p-3 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl flex justify-between items-start text-xs gap-2 dark:bg-[#273244] dark:border-[#374457]"
                    >
                      <div className="flex gap-2 items-start">
                        <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${alertDotColor[alt.type] || "bg-[#7CC3E5]"}`} />
                        <div>
                          <p className="text-[#374151] font-medium dark:text-slate-200">{alt.message}</p>
                          <span className="text-[10px] text-[#9CA3AF]">{alt.timestamp}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => dismissAlert(alt.id)}
                        data-testid={`alert-dismiss-${alt.id}`}
                        className="text-[#C9CDD4] hover:text-[#6B7280] transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

// ==========================================
// MAIN DASHBOARD & MONITORING VIEW
// ==========================================

const roleStyles = {
  admin: "bg-[#FCE6E9] text-[#B2607F] border-[#F8C8DC]",
  teacher: "bg-[#E6F2FA] text-[#3D7E9E] border-[#B3E0F2]",
  parent: "bg-[#E2F4E1] text-[#2F6031] border-[#C4E5C2]",
};

function Dashboard({ onOpenCare, onOpenStore, onHome, onSignOut, onMasterDash, onOpenInbox, onFacilityDash, onSiteNav }) {
  const { isProductionMode, ownerData, profiles, children, invoices, currentUser, pushAlert, deleteProfile, deleteChild, refreshFromStorage, rooms, checkins, meetings, messages, wsActions } = useApp();
  const [editingProfile, setEditingProfile] = useState(null);
  const [editingChild, setEditingChild] = useState(null);
  const [showAvatarEdit, setShowAvatarEdit] = useState(false);
  const [detailProfile, setDetailProfile] = useState(null);
  const [detailChild, setDetailChild] = useState(null);
  const [lastSync, setLastSync] = useState(() => new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit" }));
  const [autoSync, setAutoSync] = useState(true);

  const syncNow = useCallback(() => {
    refreshFromStorage();
    setLastSync(new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit" }));
  }, [refreshFromStorage]);

  useEffect(() => {
    if (!autoSync) return;
    const t = setInterval(syncNow, 15000);
    return () => clearInterval(t);
  }, [autoSync, syncNow]);
  const rawRole = currentUser?.role;
  const role = rawRole === "master" || rawRole === "daycare_admin" ? "admin" : rawRole === "staff" ? "teacher" : "parent";
  const isParent = role === "parent";
  const myChild = children.find((c) => c.id === "ch_1") || children[0];
  const [isOwnerModalOpen, setIsOwnerModalOpen] = useState(false);
  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
  // Compact mode: condensed single-column-ish dashboard, defaults ON on phones, persisted per device
  const [compactMode, setCompactMode] = useState(() => {
    const saved = localStorage.getItem("nt_compact");
    return saved === null ? window.innerWidth < 640 : saved === "1";
  });
  useEffect(() => {
    localStorage.setItem("nt_compact", compactMode ? "1" : "0");
  }, [compactMode]);

  const metrics = [
    {
      label: "Active Profiles",
      value: profiles.length,
      sub: "Staff & Parent accounts",
      icon: Users,
      iconWrap: "bg-[#FCE6E9]",
      iconColor: "text-[#E5A0BC]",
      testId: "metric-profiles",
    },
    {
      label: "Enrolled Children",
      value: children.length,
      sub: ownerData?.maxCapacity ? `Capacity: ${children.length}/${ownerData.maxCapacity}` : "No capacity specified",
      icon: Baby,
      iconWrap: "bg-[#E6F2FA]",
      iconColor: "text-[#5CB3DC]",
      testId: "metric-children",
    },
    {
      label: "Active Invoices",
      value: invoices.length,
      sub: "Billing & Tuition statements",
      icon: CreditCard,
      iconWrap: "bg-[#FFF2D1]",
      iconColor: "text-[#D9A93E]",
      testId: "metric-invoices",
    },
    {
      label: "System Status",
      value: "99.98%",
      sub: "Multi-tenant RLS Operational",
      icon: Activity,
      iconWrap: "bg-[#E2F4E1]",
      iconColor: "text-[#7CC98F]",
      testId: "metric-status",
    },
  ];

  return (
    <div className="flex flex-col min-h-screen" data-testid="dashboard-root">
      <Header onOpenOwnerModal={() => setIsOwnerModalOpen(true)} onOpenEnrollModal={() => setIsEnrollModalOpen(true)} onOpenCare={onOpenCare} onOpenStore={onOpenStore} onHome={onHome} onSignOut={onSignOut} onEditAvatar={() => setShowAvatarEdit(true)} onMasterDash={onMasterDash} onOpenInbox={onOpenInbox} onFacilityDash={onFacilityDash} onSiteNav={onSiteNav} />

      <main className={`flex-1 px-4 sm:px-6 lg:px-8 max-w-7xl w-full mx-auto ${compactMode ? "py-4 space-y-4" : "py-8 space-y-8"}`}>
        {/* Refresh controls */}
        <div className="flex items-center justify-end gap-2 flex-wrap" data-testid="dash-refresh-controls">
          <span className="text-[10px] text-[#9CA3AF] font-medium" data-testid="dash-last-synced">
            Last synced {lastSync} {autoSync && "· auto every 15s"}
          </span>
          <button
            onClick={() => setCompactMode((c) => !c)}
            data-testid="compact-mode-toggle"
            title="Condensed view — tighter spacing, smaller cards (great on phones)"
            className={`px-3.5 py-1.5 rounded-full text-[11px] font-bold border transition-all ${
              compactMode ? "bg-[#E6F2FA] border-[#B3E0F2] text-[#3D7E9E]" : "bg-white border-[#FCE6E9] text-[#9CA3AF] dark:bg-[#273244] dark:border-[#374457]"
            }`}
          >
            Compact {compactMode ? "On" : "Off"}
          </button>
          <button
            onClick={() => setAutoSync((a) => !a)}
            data-testid="dash-auto-sync-toggle"
            className={`px-3.5 py-1.5 rounded-full text-[11px] font-bold border transition-all ${
              autoSync ? "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]" : "bg-white border-[#FCE6E9] text-[#9CA3AF] dark:bg-[#273244] dark:border-[#374457]"
            }`}
          >
            Auto 15s {autoSync ? "On" : "Off"}
          </button>
          <button
            onClick={syncNow}
            data-testid="dash-refresh-btn"
            className="flex items-center gap-1.5 px-3.5 py-1.5 bg-[#B3E0F2] hover:bg-[#9ACCE0] text-[#374151] rounded-full text-[11px] font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-sm shadow-blue-200"
          >
            Refresh
          </button>
        </div>

        {/* Facility Status Verification Banner (staff & admin) */}
        {!isParent && (ownerData ? (
          <div
            className="p-5 bg-white border border-[#E6F2FA] rounded-3xl flex items-center justify-between shadow-sm shadow-blue-100/60 animate-fade-up dark:bg-[#232D3D] dark:border-[#374457]"
            data-testid="facility-verified-banner"
          >
            <div className="flex items-center gap-4">
              <span className="w-12 h-12 rounded-full bg-[#E6F2FA] flex items-center justify-center shrink-0">
                <ShieldCheck className="w-6 h-6 text-[#5CB3DC]" />
              </span>
              <div>
                <h3 className="text-base font-extrabold text-[#374151] font-heading dark:text-slate-100">{ownerData.facilityName || "Facility"}</h3>
                <p className="text-xs text-[#6B7280] mt-0.5 dark:text-slate-400">
                  {ownerData.licenseNumber && (
                    <>
                      License: <span className="font-mono font-semibold">{ownerData.licenseNumber}</span>
                      {ownerData.jurisdiction ? ` (${ownerData.jurisdiction})` : ""}
                    </>
                  )}
                  {ownerData.licenseNumber && ownerData.maxCapacity ? " · " : ""}
                  {ownerData.maxCapacity ? `Max Capacity: ${ownerData.maxCapacity} children` : ""}
                </p>
              </div>
            </div>
            <span
              className="px-3.5 py-1.5 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] text-xs font-bold rounded-full shrink-0"
              data-testid="facility-verified-badge"
            >
              Active & Verified
            </span>
          </div>
        ) : (
          <div
            className="p-5 bg-[#FFF9F2] border border-[#F5E3B3] rounded-3xl flex items-center justify-between shadow-sm animate-fade-up dark:bg-[#232D3D] dark:border-[#374457]"
            data-testid="facility-unregistered-banner"
          >
            <div className="flex items-center gap-4">
              <span className="w-12 h-12 rounded-full bg-[#FFF2D1] flex items-center justify-center shrink-0">
                <AlertTriangle className="w-6 h-6 text-[#D9A93E]" />
              </span>
              <div>
                <h3 className="text-base font-extrabold text-[#7A5A17] font-heading dark:text-amber-200">Unregistered Daycare Shell</h3>
                <p className="text-xs text-[#A08244] mt-0.5 dark:text-amber-100/70">
                  No state licensing details provided for this tenant.
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOwnerModalOpen(true)}
              data-testid="complete-onboarding-btn"
              className="px-4 py-2.5 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] font-bold text-xs rounded-full transition-all duration-200 hover:-translate-y-0.5 shadow-sm shadow-pink-200 shrink-0"
            >
              Complete Onboarding
            </button>
          </div>
        ))}

        {/* Parent Welcome */}
        {isParent && (
          <div className="p-5 bg-gradient-to-r from-[#FCE6E9] to-[#E6F2FA] border border-[#F8C8DC] rounded-3xl flex items-center gap-4 animate-fade-up dark:from-[#273244] dark:to-[#232D3D] dark:border-[#374457]" data-testid="parent-welcome">
            <span className="w-12 h-12 rounded-full bg-white/80 flex items-center justify-center shrink-0 shadow-sm">
              <Baby className="w-6 h-6 text-[#E5A0BC]" />
            </span>
            <div>
              <h3 className="text-base font-extrabold font-heading text-[#374151] dark:text-slate-100">
                Welcome back, {currentUser?.fullName?.split(" ")[0]}
              </h3>
              <p className="text-xs text-[#6B7280] mt-0.5 dark:text-slate-400">
                Here’s {myChild ? `${myChild.firstName}'s` : "your child's"} day at a glance — media, documents, and billing below.
              </p>
            </div>
          </div>
        )}

        {/* Quick Metrics Grid */}
        {!isParent && (
        <div className={`grid ${compactMode ? "grid-cols-2 gap-3" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"}`}>
          {metrics.map((m, i) => (
            <div
              key={m.testId}
              data-testid={m.testId}
              className={`${compactMode ? "p-4" : "p-6"} bg-white border border-[#FCE6E9] rounded-3xl shadow-sm shadow-pink-100/50 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 animate-fade-up stagger-${i + 1} dark:bg-[#232D3D] dark:border-[#374457] dark:shadow-none`}
            >
              <div className={`flex justify-between items-start ${compactMode ? "mb-2" : "mb-4"}`}>
                <span className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider dark:text-slate-400">{m.label}</span>
                <span className={`${compactMode ? "w-9 h-9" : "w-11 h-11"} rounded-full ${m.iconWrap} flex items-center justify-center`}>
                  <m.icon className={`${compactMode ? "w-4 h-4" : "w-5 h-5"} ${m.iconColor}`} />
                </span>
              </div>
              <div className={`${compactMode ? "text-2xl" : "text-3xl"} font-black font-heading text-[#374151] dark:text-slate-100`} data-testid={`${m.testId}-value`}>
                {m.value}
              </div>
              {!compactMode && <p className="text-xs text-[#9CA3AF] mt-1.5 font-medium dark:text-slate-500">{m.sub}</p>}
            </div>
          ))}
        </div>
        )}

        {/* Care Connections Entry */}
        <div
          className="p-6 bg-gradient-to-r from-[#FCE6E9] to-[#E6F2FA] border border-[#F8C8DC] rounded-3xl flex items-center justify-between gap-4 flex-wrap shadow-sm shadow-pink-100/50 animate-fade-up dark:from-[#273244] dark:to-[#232D3D] dark:border-[#374457]"
          data-testid="care-connections-card"
        >
          <div className="flex items-center gap-4">
            <span className="w-12 h-12 rounded-full bg-white/80 flex items-center justify-center shrink-0 shadow-sm">
              <HeartHandshake className="w-6 h-6 text-[#E5A0BC]" />
            </span>
            <div>
              <h3 className="text-base font-extrabold font-heading text-[#374151] dark:text-slate-100">Care Connections</h3>
              <p className="text-xs text-[#6B7280] mt-0.5 dark:text-slate-400">
                Find qualified caregivers for additional care needs — respite, babysitting, companion care and more.
              </p>
            </div>
          </div>
          <button
            onClick={onOpenCare}
            data-testid="open-care-connections-btn"
            className="px-5 py-2.5 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-xs font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-sm shadow-pink-200"
          >
            Explore Care Connections
          </button>
        </div>

        {/* Sponsor ads: dashboard placement */}
        <AdSlot placement="dashboard" />

        {/* Daycare Operations Modules */}
        <DaycareOps
          children={isParent && myChild ? [myChild] : children}
          allowedTabs={
            role === "admin"
              ? undefined
              : role === "teacher"
              ? ["media", "pickup", "documents", "applications", "ratios", "rooms", "timeclock", "live"]
              : ["media", "documents", "applications"]
          }
          onAlert={pushAlert}
          currentUserName={currentUser?.fullName}
          currentUserEmail={currentUser?.email}
          facilityName={ownerData?.facilityName}
          allChildren={children}
          rooms={rooms}
          invoices={invoices}
          ws={wsActions}
        />

        {/* Parent connect: check-in/out, video meetings, staff messaging */}
        {isParent ? (
          <ParentConnect mode="parent" myChildren={children} checkins={checkins} meetings={meetings} messages={messages} currentUser={currentUser} ws={wsActions} />
        ) : (
          <ParentConnect mode="staff" messages={messages} currentUser={currentUser} ws={wsActions} />
        )}

        {/* Records & Management Tables */}
        {isParent ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* My Child Panel */}
            <div className="bg-white border border-[#E6F2FA] rounded-3xl p-6 shadow-sm shadow-blue-100/50 dark:bg-[#232D3D] dark:border-[#374457] dark:shadow-none" data-testid="my-child-panel">
              <h3 className="text-base font-extrabold text-[#374151] flex items-center gap-2.5 font-heading dark:text-slate-100 mb-5">
                <span className="w-9 h-9 rounded-full bg-[#E6F2FA] flex items-center justify-center">
                  <Baby className="w-4 h-4 text-[#5CB3DC]" />
                </span>
                My Child
              </h3>
              {!myChild ? (
                <p className="text-xs text-[#9CA3AF] font-medium" data-testid="my-child-empty">No child linked to this account yet.</p>
              ) : (
                <div className="p-4 bg-[#FFF9F2] border border-[#E6F2FA] rounded-2xl dark:bg-[#273244] dark:border-[#374457]">
                  <div className="flex items-center gap-3.5">
                    <div className="w-14 h-14 rounded-full bg-white border-2 border-[#B3E0F2] overflow-hidden flex items-center justify-center shrink-0 shadow-sm">
                      {myChild.photoBase64 ? (
                        <img src={myChild.photoBase64} alt={myChild.firstName} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-lg font-black text-[#5CB3DC] font-heading">{myChild.firstName.charAt(0)}</span>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-extrabold text-[#374151] dark:text-slate-100">
                        {myChild.firstName} {myChild.lastName}
                      </p>
                      <p className="text-xs text-[#9CA3AF]">DOB: {myChild.dob}</p>
                      <div className="flex gap-1.5 mt-1.5 flex-wrap">
                        {(myChild.allergies || []).map((a, i) => (
                          <span key={a} className="px-2 py-0.5 bg-rose-50 border border-rose-200 text-rose-500 text-[10px] rounded-full font-bold">
                            {a}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  {myChild.medicalNotes && (
                    <p className="text-xs text-[#6B7280] mt-3 p-3 bg-white border border-[#FCE6E9] rounded-xl dark:bg-[#232D3D] dark:border-[#374457] dark:text-slate-300">
                      <strong>Medical notes:</strong> {myChild.medicalNotes}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* My Invoices Panel */}
            <div className="bg-white border border-[#FCE6E9] rounded-3xl p-6 shadow-sm shadow-pink-100/50 dark:bg-[#232D3D] dark:border-[#374457] dark:shadow-none" data-testid="my-invoices-panel">
              <h3 className="text-base font-extrabold text-[#374151] flex items-center gap-2.5 font-heading dark:text-slate-100 mb-5">
                <span className="w-9 h-9 rounded-full bg-[#FFF2D1] flex items-center justify-center">
                  <CreditCard className="w-4 h-4 text-[#D9A93E]" />
                </span>
                My Invoices
              </h3>
              {invoices.length === 0 ? (
                <p className="text-xs text-[#9CA3AF] font-medium" data-testid="invoices-empty">No invoices yet.</p>
              ) : (
                <div className="space-y-3">
                  {invoices.map((inv) => (
                    <div key={inv.id} className="p-3.5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl flex items-center justify-between gap-3 flex-wrap dark:bg-[#273244] dark:border-[#374457]" data-testid={`invoice-row-${inv.id}`}>
                      <div>
                        <p className="text-xs font-extrabold text-[#374151] dark:text-slate-100">{inv.description || (inv.items || []).map((i) => i.label).join(", ") || "Invoice"}</p>
                        <p className="text-[11px] text-[#9CA3AF]">Due {inv.dueDate || inv.period || "—"}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-black font-heading text-[#374151] dark:text-slate-100">${inv.amount.toFixed(2)}</p>
                        <span
                          className={`px-2.5 py-0.5 border text-[10px] font-extrabold uppercase rounded-full ${
                            inv.status === "paid"
                              ? "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]"
                              : inv.status === "pending"
                              ? "bg-[#FFF2D1] border-[#F5E3B3] text-[#7A5A17]"
                              : "bg-rose-50 border-rose-200 text-rose-500"
                          }`}
                          data-testid={`invoice-status-${inv.id}`}
                        >
                          {inv.status.replace("_", " ")}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Staff / Profiles Panel */}
          <div
            className="bg-white border border-[#FCE6E9] rounded-3xl p-6 shadow-sm shadow-pink-100/50 dark:bg-[#232D3D] dark:border-[#374457] dark:shadow-none"
            data-testid="user-directory-panel"
          >
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-base font-extrabold text-[#374151] flex items-center gap-2.5 font-heading dark:text-slate-100">
                <span className="w-9 h-9 rounded-full bg-[#FCE6E9] flex items-center justify-center">
                  <Users className="w-4 h-4 text-[#E5A0BC]" />
                </span>
                User Directory
              </h3>
              <span className="text-xs text-[#9CA3AF] font-semibold" data-testid="user-directory-count">
                {profiles.length} items
              </span>
            </div>
            {profiles.length === 0 ? (
              <div
                className="p-10 border-2 border-dashed border-[#FCE6E9] rounded-3xl text-center dark:border-[#374457]"
                data-testid="user-directory-empty"
              >
                <p className="text-xs text-[#9CA3AF] font-medium">No active user profiles found. Waiting for live onboarding...</p>
              </div>
            ) : (
              <div className="space-y-3">
                {profiles.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`user-row-${p.id}`}
                    onClick={() => setDetailProfile(p)}
                    className="p-3.5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl flex items-center justify-between cursor-pointer hover:bg-[#FCE6E9]/40 transition-colors duration-200 dark:bg-[#273244] dark:border-[#374457] dark:hover:bg-[#2f3c50]"
                  >
                    <div className="flex items-center gap-3.5">
                      <div className="w-11 h-11 rounded-full bg-white border-2 border-[#FCE6E9] overflow-hidden flex items-center justify-center shrink-0 shadow-sm dark:border-[#374457] dark:bg-[#1F2937]">
                        {p.avatarBase64 ? (
                          <img src={p.avatarBase64} alt={p.fullName} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-sm font-black text-[#E5A0BC] font-heading">{p.fullName.charAt(0)}</span>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#374151] dark:text-slate-100">{p.fullName}</p>
                        <p className="text-xs text-[#9CA3AF]">{p.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-3 py-1 border text-[10px] font-extrabold uppercase tracking-wide rounded-full ${roleStyles[p.role] || roleStyles.teacher}`}
                        data-testid={`user-role-${p.id}`}
                      >
                        {p.role}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingProfile(p);
                        }}
                        data-testid={`edit-profile-btn-${p.id}`}
                        title="Edit profile"
                        className="w-7 h-7 rounded-full flex items-center justify-center text-[#9CA3AF] hover:text-[#E5A0BC] hover:bg-[#FCE6E9] transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Children Roster Panel */}
          <div
            className="bg-white border border-[#E6F2FA] rounded-3xl p-6 shadow-sm shadow-blue-100/50 dark:bg-[#232D3D] dark:border-[#374457] dark:shadow-none"
            data-testid="children-roster-panel"
          >
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-base font-extrabold text-[#374151] flex items-center gap-2.5 font-heading dark:text-slate-100">
                <span className="w-9 h-9 rounded-full bg-[#E6F2FA] flex items-center justify-center">
                  <Baby className="w-4 h-4 text-[#5CB3DC]" />
                </span>
                Children Roster
              </h3>
              <span className="text-xs text-[#9CA3AF] font-semibold" data-testid="children-roster-count">
                {children.length} items
              </span>
            </div>
            {children.length === 0 ? (
              <div
                className="p-10 border-2 border-dashed border-[#E6F2FA] rounded-3xl text-center dark:border-[#374457]"
                data-testid="children-roster-empty"
              >
                <p className="text-xs text-[#9CA3AF] font-medium">No child records registered. Ready for input...</p>
              </div>
            ) : (
              <div className="space-y-3">
                {children.map((c) => (
                  <div
                    key={c.id}
                    data-testid={`child-row-${c.id}`}
                    onClick={() => setDetailChild(c)}
                    className="p-3.5 bg-[#FFF9F2] border border-[#E6F2FA] rounded-2xl flex items-center justify-between cursor-pointer hover:bg-[#E6F2FA]/40 transition-colors duration-200 dark:bg-[#273244] dark:border-[#374457] dark:hover:bg-[#2f3c50]"
                  >
                    <div className="flex items-center gap-3.5">
                      <div className="w-11 h-11 rounded-full bg-white border-2 border-[#B3E0F2] overflow-hidden flex items-center justify-center shrink-0 shadow-sm dark:border-[#374457] dark:bg-[#1F2937]">
                        {c.photoBase64 ? (
                          <img src={c.photoBase64} alt={c.firstName} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-sm font-black text-[#5CB3DC] font-heading">{c.firstName.charAt(0)}</span>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#374151] dark:text-slate-100">
                          {c.firstName} {c.lastName}
                        </p>
                        <p className="text-xs text-[#9CA3AF]">DOB: {c.dob}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1.5 flex-wrap justify-end">
                        {(c.allergies || []).map((alg, idx) => (
                          <span
                            key={`${c.id}-${alg}`}
                            className="px-2 py-0.5 bg-rose-50 border border-rose-200 text-rose-500 text-[10px] rounded-full font-bold"
                            data-testid={`child-allergy-${c.id}-${idx}`}
                          >
                            {alg}
                          </span>
                        ))}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingChild(c);
                        }}
                        data-testid={`edit-child-btn-${c.id}`}
                        title="Edit child"
                        className="w-7 h-7 rounded-full flex items-center justify-center text-[#9CA3AF] hover:text-[#5CB3DC] hover:bg-[#E6F2FA] transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        )}
      </main>

      <DaycareOnboardingModal isOpen={isOwnerModalOpen} onClose={() => setIsOwnerModalOpen(false)} />
      <MemberEnrollmentModal isOpen={isEnrollModalOpen} onClose={() => setIsEnrollModalOpen(false)} />
      {editingProfile && <EditProfileModal profile={editingProfile} onClose={() => setEditingProfile(null)} />}
      {editingChild && <EditChildModal child={editingChild} onClose={() => setEditingChild(null)} />}
      {showAvatarEdit && <AvatarEditModal onClose={() => setShowAvatarEdit(false)} />}
      {detailProfile && (
        <RecordDetailModal
          testId="profile-detail-modal"
          title="Profile Details"
          image={detailProfile.avatarBase64}
          name={detailProfile.fullName}
          rows={[
            ["Email", detailProfile.email],
            ["Phone", detailProfile.phone],
            ["Role", detailProfile.role],
            ["Status", detailProfile.isActive ? "Active" : "Inactive"],
            ["Organization", detailProfile.organizationId],
          ]}
          contact={{ phone: detailProfile.phone, email: detailProfile.email }}
          socials={detailProfile.socials}
          shareId={detailProfile.id}
          onEdit={() => {
            setEditingProfile(detailProfile);
            setDetailProfile(null);
          }}
          onDelete={() => {
            deleteProfile(detailProfile.id);
            toast.success(`${detailProfile.fullName} removed from directory.`);
            setDetailProfile(null);
          }}
          onClose={() => setDetailProfile(null)}
        />
      )}
      {detailChild && (
        <RecordDetailModal
          testId="child-detail-modal"
          title="Child Record"
          image={detailChild.photoBase64}
          name={`${detailChild.firstName} ${detailChild.lastName}`}
          rows={[
            ["Date of Birth", detailChild.dob],
            ["Classroom", detailChild.classroomId],
            ["Allergies", detailChild.allergies.join(", ") || "None"],
            ["Medical Notes", detailChild.medicalNotes || "None"],
            ["Emergency Contacts", detailChild.emergencyContacts.map((ec) => `${ec.name} (${ec.relation})`).join(", ") || "None"],
          ]}
          onEdit={() => {
            setEditingChild(detailChild);
            setDetailChild(null);
          }}
          onDelete={() => {
            deleteChild(detailChild.id);
            toast.success(`${detailChild.firstName} ${detailChild.lastName} removed from roster.`);
            setDetailChild(null);
          }}
          onClose={() => setDetailChild(null)}
        />
      )}
    </div>
  );
}

function Shell() {
  const { currentUser, accountsDeleted, signIn, signOut, applyTheme, theme, toggleTheme, updateCurrentUserAvatar } = useApp();
  const [view, setView] = useState("landing");
  const [careTab, setCareTab] = useState(null);

  const viewHistory = useRef([]);

  // URL-aware navigation (deep-linkable + browser back/forward); every navigate records the previous view
  const navigate = (v, { back = false } = {}) => {
    if (v === "facility") v = "facility-dash";
    if (!back && v !== view) viewHistory.current.push(view);
    setView(v);
    const path = v === "landing" ? "/" : v === "payment-success" ? "/payment/success" : v === "payment-cancel" ? "/payment/cancel" : v === "facility-dash" ? "/facility" : `/${v}`;
    if (window.location.pathname !== path) window.history.pushState({}, "", path);
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  };

  // Global site search: jump to a view, then smooth-scroll to a panel anchor (master dashboard sections)
  const siteNav = ({ view: v, anchor }) => {
    navigate(v);
    if (!anchor) return;
    let tries = 0;
    const attempt = () => {
      const el = document.getElementById(anchor) || document.querySelector(`[data-testid="${anchor}"]`);
      if (el) {
        if (anchor === "user-directory") window.dispatchEvent(new Event("nt-open-directory"));
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (tries++ < 12) {
        setTimeout(attempt, 250);
      }
    };
    setTimeout(attempt, 300);
  };

  // Back buttons return to the last page visited instead of resetting to home
  const goBack = () => {
    let prev = viewHistory.current.pop();
    if (!prev || prev === view) {
      prev = currentUser ? (currentUser.role === "master" ? "master" : currentUser.role === "caregiver" ? "care" : "dashboard") : "landing";
    }
    navigate(prev, { back: true });
  };

  const pathToView = (p) => ({ about: "about", contact: "contact", store: "store", providers: "providers", terms: "terms", privacy: "privacy", care: "care", dashboard: "dashboard", master: "master", facility: "facility-dash", "facility-dash": "facility-dash", "admin-inbox": "admin-inbox", "payment/success": "payment-success", "payment/cancel": "payment-cancel" }[p]);

  useEffect(() => {
    const initial = window.location.pathname.replace(/^\//, "");
    const initialView = pathToView(initial);
    if (initialView) setView(initialView);
    const onPop = () => {
      const p = window.location.pathname.replace(/^\//, "");
      setView(pathToView(p) || "landing");
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Per-page SEO metadata (title/description/OG) — master-editable overrides via Site Content
  useEffect(() => {
    applyPageMeta(view);
  }, [view]);

  // Native video meetings: opened via window event or a shared ?meet= link
  const [meetingRoom, setMeetingRoom] = useState(null);
  const [liveInvite, setLiveInvite] = useState(null);
  const [publicLive, setPublicLive] = useState(null);
  useEffect(() => {
    const onOpen = (e) => setMeetingRoom(e.detail?.room || null);
    window.addEventListener("nt-open-meeting", onOpen);
    const urlMeet = new URLSearchParams(window.location.search).get("meet");
    if (urlMeet) setMeetingRoom(urlMeet);
    return () => window.removeEventListener("nt-open-meeting", onOpen);
  }, []);

  // Shared live-event links (?live=<id>&key=<access>) open the viewer directly — invited users join without sign-in
  const [publicLiveKey, setPublicLiveKey] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlLive = params.get("live");
    if (urlLive) {
      setPublicLive(urlLive);
      setPublicLiveKey(params.get("key"));
    }
  }, []);

  // Live-event co-view: floating picture-in-picture player that survives navigation
  const [pipLive, setPipLive] = useState(null);
  useEffect(() => {
    const onPip = (e) => setPipLive(e.detail || null);
    window.addEventListener("nt-pip-live", onPip);
    return () => window.removeEventListener("nt-pip-live", onPip);
  }, []);
  const [showSignIn, setShowSignIn] = useState(false);
  const [ownerModalOpen, setOwnerModalOpen] = useState(false);
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  const [enrollTab, setEnrollTab] = useState("staff");
  const [claimToken, setClaimToken] = useState(null);
  const [resetToken, setResetToken] = useState(null);
  const googleRan = useRef(false);

  // Handle Emergent Google OAuth callback (#session_id=...) — must run before the session-restore probe
  useEffect(() => {
    if (googleRan.current) return;
    const hash = window.location.hash;
    if (!hash.includes("session_id=")) return;
    googleRan.current = true;
    const sessionId = hash.split("session_id=")[1].split("&")[0];
    window.history.replaceState({}, "", window.location.pathname);
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/google/session`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId }),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Google sign-in failed");
        toast.success(`Welcome, ${body.name}!`);
        handleRealLogin(body);
      } catch (err) {
        toast.error(err.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle invite claim links (/?claim=TOKEN), password reset links (/?reset=TOKEN), and secure-PIN reset links (/?pin-reset=TOKEN)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const claim = params.get("claim");
    const reset = params.get("reset");
    const pinReset = params.get("pin-reset");
    if (claim) setClaimToken(claim);
    if (reset) setResetToken(reset);
    if (pinReset) {
      (async () => {
        try {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/secure-pin/reset`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: pinReset }),
          });
          const body = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "PIN reset link invalid or expired");
          if (body.pin) toast.warning(`Email is rate-limited — your new secure PIN is ${body.pin} (shown once — save it now)`);
          else toast.success("Verified — your new secure inbox PIN was emailed to you.");
        } catch (err) {
          toast.error(err.message);
        }
      })();
    }
    if (claim || reset || pinReset) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  // Handle email activation links (/?activate=TOKEN)
  const activateRan = useRef(false);
  useEffect(() => {
    if (activateRan.current) return;
    activateRan.current = true;
    const token = new URLSearchParams(window.location.search).get("activate");
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/onboarding/activate/${token}`);
        const body = await res.json();
        if (res.ok) toast.success(`${body.facilityName} is activated! The owner can now complete setup.`);
        else toast.error(typeof body.detail === "string" ? body.detail : "Activation link invalid or already used.");
      } catch {
        toast.error("Activation failed. Please try again.");
      }
      window.history.replaceState({}, "", window.location.pathname);
    })();
  }, []);

  useEffect(() => {
    if (!currentUser && !["landing", "about", "contact", "store", "providers", "terms", "privacy", "payment-success", "payment-cancel", "care"].includes(view)) setView("landing");
  }, [view, currentUser]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path: view }),
    }).catch(() => {});
  }, [view]);

  // Restore session from httpOnly cookie on mount so refresh keeps master signed in
  useEffect(() => {
    if (googleRan.current) return; // Google OAuth callback exchanges its session first
    (async () => {
      try {
        let res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { credentials: "include" });
        if (res.status === 401) {
          // Access token (1h) may be expired while the 7-day refresh cookie is still valid — renew and retry once
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`, { method: "POST", credentials: "include" }).catch(() => {});
          res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { credentials: "include" });
        }
        if (res.ok) {
          const u = await res.json();
          const role = u.role === "master" ? "master" : u.role;
          signIn({ id: u.id, fullName: u.name, email: u.email, role, position: u.position, childIds: u.childIds || [], avatar: u.avatar || null, permissions: u.permissions || [], real: true });
          if (u.theme) applyTheme(u.theme);
          const saved = sessionStorage.getItem("nt_view");
          const urlView = pathToView(window.location.pathname.replace(/^\//, ""));
          // Master accounts always land on Master Control — a stale saved view must never demote them to the parent dashboard
          const masterOk = ["master", "store", "care"].includes(saved);
          if (!urlView) setView(role === "master" ? (masterOk ? saved : "master") : saved && ["dashboard", "care", "store", "landing"].includes(saved) ? saved : role === "caregiver" ? "care" : "dashboard");
        }
      } catch (err) {
        console.debug("No active session to restore", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the 1-hour access token alive while signed in: silent refresh every 10 min + on tab focus,
  // so long sessions never hit "Token expired" on actions like sending invites
  useEffect(() => {
    if (!currentUser?.real) return;
    const refresh = () =>
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`, { method: "POST", credentials: "include" }).catch(() => {});
    refresh();
    const iv = setInterval(refresh, 10 * 60 * 1000);
    window.addEventListener("focus", refresh);
    return () => {
      clearInterval(iv);
      window.removeEventListener("focus", refresh);
    };
  }, [currentUser?.real]);

  // Poll for instant live invites addressed to me — the in-app safety net when invite emails are rate-limited
  useEffect(() => {
    if (!currentUser?.real) {
      setLiveInvite(null);
      return;
    }
    const check = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/mine`, { credentials: "include" });
        const d = await r.json().catch(() => ({}));
        const hot = (d.meetings || []).find((m) => m.instant);
        setLiveInvite(hot || null);
      } catch {}
    };
    check();
    const iv = setInterval(check, 60000);
    window.addEventListener("focus", check);
    return () => {
      clearInterval(iv);
      window.removeEventListener("focus", check);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.real]);

  // Public community live events started from the profile video panel open the shared viewer
  useEffect(() => {
    const h = (e) => setPublicLive(e.detail?.eventId || null);
    window.addEventListener("nt-open-public-live", h);
    return () => window.removeEventListener("nt-open-public-live", h);
  }, []);

  useEffect(() => {
    // don't clobber the saved view with the initial anonymous "landing" state
    if (currentUser || view !== "landing") sessionStorage.setItem("nt_view", view);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const openSignIn = () => setShowSignIn(true);
  const handleSignInSelect = null;
  const handleRealLogin = (user) => {
    const role = user.role === "master" ? "master" : user.role;
    signIn({ id: user.id, fullName: user.name, email: user.email, role, position: user.position, childIds: user.childIds || [], avatar: user.avatar || null, permissions: user.permissions || [], real: true });
    if (user.theme) applyTheme(user.theme);
    setShowSignIn(false);
    sessionStorage.removeItem("nt_view"); // fresh login always lands on the role-derived home — no stale view overrides
    setView(view === "store" ? "store" : role === "master" ? "master" : role === "caregiver" ? "care" : "dashboard");
  };
  const handleSignOut = () => {
    if (currentUser?.real) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    }
    signOut();
    setView("landing");
  };
  const [showAvatarEdit, setShowAvatarEdit] = useState(false);
  const openAvatarEdit = () => setShowAvatarEdit(true);
  const [siteStatus, setSiteStatus] = useState(null);
  const [lockOpen, setLockOpen] = useState(false);
  const [lockPin, setLockPin] = useState("");
  const [lockError, setLockError] = useState("");
  const [lockBusy, setLockBusy] = useState(false);

  useEffect(() => {
    if (!currentUser?.real) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site/status`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setSiteStatus)
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.real]);

  // Caregiver profiles are simple by design — they never enter facility dashboards
  useEffect(() => {
    if (view === "dashboard" && currentUser?.real && currentUser.role === "caregiver") setView("care");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, currentUser?.role]);

  const goMasterControl = async () => {
    sessionStorage.setItem("nt_view", "master");
    if (currentUser.role !== "master") {
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/account/roles/switch`, {
          method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ role: "master" }),
        });
      } catch { /* proceed */ }
      window.location.reload(); // rehydrate session with the master role active
      return;
    }
    setView("master");
  };

  const submitUnlock = async () => {
    setLockBusy(true);
    setLockError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/master/unlock`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pin: lockPin }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        sessionStorage.setItem("nt_master_unlocked", "1");
        setLockOpen(false);
        toast.success("Master Control unlocked.");
        goMasterControl();
        return;
      }
      setLockError(typeof body.detail === "string" ? body.detail : "Incorrect PIN");
    } finally {
      setLockBusy(false);
    }
  };

  return (
    <>
      <ErrorBoundary resetKey={view} onHome={() => navigate("landing")}>
      {view === "landing" && (
        <Landing
          onSignIn={openSignIn}
          onEnterCare={(tab) => { setCareTab(typeof tab === "string" ? tab : null); navigate("care"); }}
          currentUser={currentUser}
          onDashboard={() => setView("dashboard")}
          onSignOut={handleSignOut}
          onAbout={() => navigate("about")}
          onContact={() => navigate("contact")}
          onStore={() => navigate("store")}
          onOpenDirectory={() => navigate("providers")}
          onOpenTerms={() => navigate("terms")}
          onOpenPrivacy={() => navigate("privacy")}
        />
      )}
      {view === "about" && <About onBack={goBack} onContact={() => navigate("contact")} />}
      {view === "contact" && <ContactPage onBack={goBack} />}
      {view === "providers" && <ProviderDirectory currentUser={currentUser} onBack={goBack} onHome={() => navigate("landing")} />}
      {view === "terms" && <LegalPage page="terms" currentUser={currentUser} onBack={goBack} onHome={() => navigate("landing")} />}
      {view === "privacy" && <LegalPage page="privacy" currentUser={currentUser} onBack={goBack} onHome={() => navigate("landing")} />}
      {view === "store" && (
        <Storefront
          currentUser={currentUser}
          onBack={goBack}
          onRequireSignIn={openSignIn}
          onHome={() => navigate("landing")}
          onMasterDash={() => setView("master")}
        />
      )}
      {view === "payment-success" && <PaymentResult mode="success" onStore={() => navigate("store")} onHome={() => navigate("landing")} />}
      {view === "payment-cancel" && <PaymentResult mode="cancel" onStore={() => navigate("store")} onHome={() => navigate("landing")} />}
      {view === "master" && currentUser?.real && currentUser?.role === "master" && (
        <MasterDash
          currentUser={currentUser}
          onSignOut={handleSignOut}
          onOpenInbox={() => setView("admin-inbox")}
          onDashboard={() => setView("dashboard")}
          onHome={() => navigate("landing")}
          onEditAvatar={openAvatarEdit}
          onOpenOwnerModal={() => setOwnerModalOpen(true)}
          onOpenEnrollModal={(tab) => {
            setEnrollTab(tab);
            setEnrollModalOpen(true);
          }}
          theme={theme}
          onToggleTheme={toggleTheme}
          onSiteNav={siteNav}
        />
      )}
      {view === "admin-inbox" && currentUser?.real && (currentUser?.role === "master" || currentUser?.role === "daycare_admin" || (currentUser?.extra_roles || []).includes("master")) && (
        <AdminInbox currentUser={currentUser} onBack={goBack} onHome={() => navigate("landing")} />
      )}
      {view === "facility-dash" && currentUser?.real && (currentUser?.role === "daycare_admin" || currentUser?.role === "master") && (
        <FacilityDash currentUser={currentUser} onHome={() => navigate("dashboard")} />
      )}
      {view === "care" && <CareConnections onBack={goBack} onHome={() => navigate("landing")} onMasterDash={() => setView("master")} initialTab={careTab} currentUser={currentUser} onSignOut={handleSignOut} onEditAvatar={openAvatarEdit} onOpenStore={() => navigate("store")} onAvatarSync={updateCurrentUserAvatar} />}
      {view === "dashboard" && (
        <Dashboard onOpenCare={() => setView("care")} onOpenStore={() => navigate("store")} onHome={() => setView("landing")} onSignOut={handleSignOut} onMasterDash={() => setView("master")} onOpenInbox={() => setView("admin-inbox")} onFacilityDash={() => navigate("facility")} onSiteNav={siteNav} />
      )}
      </ErrorBoundary>
      <SignInModal isOpen={showSignIn} onClose={() => setShowSignIn(false)} onRealLogin={handleRealLogin} />
      {currentUser?.real && <BroadcastPopup currentUser={currentUser} />}
      <UpcomingLiveBanner />
      {meetingRoom && <NativeMeeting room={meetingRoom} onClose={() => setMeetingRoom(null)} />}
      {publicLive && <LiveViewer eventId={publicLive} accessKey={publicLiveKey} currentUser={currentUser} onClose={() => setPublicLive(null)} />}
      {liveInvite && currentUser?.real && !meetingRoom && (
        <LiveInvitePopup
          meeting={liveInvite}
          onJoin={() => {
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/${liveInvite.id}/ack`, { method: "POST", credentials: "include" }).catch(() => {});
            if (liveInvite.provider === "livekit" && liveInvite.roomSlug) openNativeMeeting(liveInvite.roomSlug);
            else window.open(liveInvite.link, "_blank", "noopener");
            setLiveInvite(null);
          }}
          onDismiss={() => {
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/${liveInvite.id}/ack`, { method: "POST", credentials: "include" }).catch(() => {});
            setLiveInvite(null);
          }}
        />
      )}
      {pipLive && <LivePiP eventId={pipLive.eventId} title={pipLive.title} onClose={() => setPipLive(null)} />}

      {/* Floating Master Control bubble — every screen, master accounts only */}
      {currentUser?.real && (currentUser.role === "master" || (currentUser.extra_roles || []).includes("master")) && view !== "master" && (
        <button
          onClick={() => {
            if (siteStatus?.live_mode && sessionStorage.getItem("nt_master_unlocked") !== "1") {
              setLockError("");
              setLockPin("");
              setLockOpen(true);
              return;
            }
            goMasterControl();
          }}
          data-testid="master-fab"
          title="Back to Master Control"
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 pl-3 pr-4 py-2.5 bg-[#374151] hover:bg-[#1F2937] text-white rounded-full shadow-lg shadow-gray-900/20 text-xs font-extrabold transition-all hover:-translate-y-0.5 animate-fade-up"
        >
          {siteStatus?.live_mode && sessionStorage.getItem("nt_master_unlocked") !== "1" ? <Lock className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />} Master Control
        </button>
      )}
      {lockOpen && (
        <div className="fixed inset-0 z-[100] bg-[#374151]/50 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in" data-testid="master-lock-modal" onClick={() => setLockOpen(false)}>
          <div className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-xs w-full p-6 shadow-xl text-center animate-modal-in" onClick={(e) => e.stopPropagation()}>
            <Lock className="w-8 h-8 text-[#374151] mx-auto mb-2" />
            <h3 className="text-base font-extrabold font-heading text-[#374151] mb-1">Master Control Locked</h3>
            <p className="text-[11px] text-[#9CA3AF] font-medium mb-4">Site is live — enter the 4-digit security PIN.</p>
            <input
              value={lockPin}
              onChange={(e) => setLockPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              onKeyDown={(e) => e.key === "Enter" && lockPin.length === 4 && submitUnlock()}
              inputMode="numeric"
              placeholder="••••"
              className="w-full text-center text-2xl tracking-[0.5em] font-black border border-[#E6F2FA] rounded-2xl py-3 mb-3 focus:outline-none focus:border-[#5CB3DC]"
              data-testid="master-lock-pin"
            />
            {lockError && <p className="text-[11px] font-bold text-rose-500 mb-3" data-testid="master-lock-error">{lockError}</p>}
            <button onClick={submitUnlock} disabled={lockPin.length !== 4 || lockBusy} className="w-full px-4 py-3 bg-[#374151] hover:bg-[#1F2937] text-white rounded-full text-sm font-extrabold transition-colors disabled:opacity-40" data-testid="master-lock-submit">
              {lockBusy ? "Verifying…" : "Unlock"}
            </button>
          </div>
        </div>
      )}
      {claimToken && (
        <ClaimModal
          token={claimToken}
          onClose={() => setClaimToken(null)}
          onClaimed={(user) => {
            setClaimToken(null);
            toast.success("Account activated — welcome to NestTrack!");
            handleRealLogin(user);
          }}
        />
      )}
      {resetToken && (
        <ResetModal
          token={resetToken}
          onClose={() => setResetToken(null)}
          onReset={(user) => {
            setResetToken(null);
            toast.success("Password updated — welcome back!");
            handleRealLogin(user);
          }}
        />
      )}
      <DaycareOnboardingModal isOpen={ownerModalOpen} onClose={() => setOwnerModalOpen(false)} />
      <MemberEnrollmentModal key={enrollTab} isOpen={enrollModalOpen} onClose={() => setEnrollModalOpen(false)} defaultTab={enrollTab} />
      {showAvatarEdit && <AvatarEditModal onClose={() => setShowAvatarEdit(false)} />}
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <Shell />
      </AppProvider>
    </ErrorBoundary>
  );
}
